/**
 * PERFS WALL — Écran de match live diffusable (portrait, sans header/footer).
 *
 * 3 scènes :
 *  - PRE-MATCH (no active session, kickoff <= 6h) : compte à rebours + carrousel joueurs
 *  - LIVE (active session) : score, chrono, événements, animation BUT
 *  - IDLE (no active session, no upcoming match) : message d'attente
 *
 * URL params :
 *  ?demo=1                 — mode démo (match fictif Istres vs Adversaire Démo, kickoff +5 min)
 *  ?kickoff=ISO            — override le timestamp de coup d'envoi
 *  ?away=NOM               — override le nom de l'équipe adverse
 *  ?away_logo=URL          — override le logo de l'équipe adverse
 *
 * Polling toutes les 2 s sur /api/match-live/active.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Search, Users, X, MapPin, Award, ArrowRight, ArrowLeft, Activity, Trophy, Target, Gift, Tv, Gamepad2, Mic, Sparkles, ShoppingBag, ChevronRight, LogIn } from "lucide-react";
import { api } from "../../lib/api";
import { useSettings } from "../../lib/useSettings";
import { toast } from "sonner";
import { useCustomerAuth } from "../../lib/useCustomerAuth";
import { MatchCountdown } from "../../components/sport/MatchCountdown";
import Flag from "../../components/sport/Flag";
import { LnhBadge } from "../../components/sport/LnhBadge";
import { LiquiMolyBadge } from "../../components/sport/LiquiMolyBadge";

const PRESENTER_PHOTO = "https://customer-assets.emergentagent.com/job_d6b30d32-0560-4729-9096-ffa84a0b3e19/artifacts/kqyl7q0a_image.png";
const PRESENTER_NAME = "Fabrice"; // Speaker IPHB — uniquement matchs à domicile
const MSC_LOGO = "https://customer-assets.emergentagent.com/job_d6b30d32-0560-4729-9096-ffa84a0b3e19/artifacts/r4wano64_image.png";

const PRIMARY = "#4F1B7C";
const SECONDARY = "#EC1F7C";
const BG_DEEP = "#0D0220";
const IPHB_LOGO = "/api/sport/asset?url=https%3A%2F%2Flnh.fr%2Fmedias%2Fsports_teams%2Fistres__logo__2023-2024.png";

function pad(n) { return n < 10 ? `0${n}` : `${n}`; }

// localStorage UUID pour identifier le supporter (vote MVP + prono)
function getVoterKey() {
  try {
    const KEY = "iphb_voter_key";
    let v = localStorage.getItem(KEY);
    if (!v) {
      v = (crypto?.randomUUID?.() || `vk_${Date.now()}_${Math.random().toString(36).slice(2)}`);
      localStorage.setItem(KEY, v);
    }
    return v;
  } catch { return `vk_anon_${Date.now()}`; }
}

// Hook : config jeux (lots/sponsors) — par session si fourni, sinon par défaut
function useGamesConfig(sessionId) {
  const [config, setConfig] = useState({});
  useEffect(() => {
    const url = `/match-live/games-config${sessionId ? `?session_id=${sessionId}` : ""}`;
    api.get(url).then((r) => setConfig(r.data?.config || {})).catch(() => setConfig({}));
  }, [sessionId]);
  return config;
}

// ---------------------------------------------------------------
// Hooks & helpers
// ---------------------------------------------------------------
function useTick(intervalMs = 1000) {
  const [, setT] = useState(0);
  useEffect(() => { const id = setInterval(() => setT((x) => x + 1), intervalMs); return () => clearInterval(id); }, [intervalMs]);
}

function useMatchClock(session) {
  useTick(500);
  return useMemo(() => {
    if (!session) return { label: "--:--", running: false, mt1: null, mt2: null, period: null, status: null };
    const startMs = session.chrono_started_at ? new Date(session.chrono_started_at).getTime() : null;
    const period = session.period;
    const status = session.status;
    const totalPaused = session.total_paused_ms || 0;

    let mt1Ms = session.period_1_duration_ms ?? null;
    let mt2Ms = session.period_2_duration_ms ?? null;

    if (!startMs) return { label: "--:--", running: false, mt1: null, mt2: null, period, status };

    let endRef;
    if (status === "halftime" && session.chrono_paused_at) endRef = new Date(session.chrono_paused_at).getTime();
    else if (status === "finished") endRef = session.finished_at ? new Date(session.finished_at).getTime() : Date.now();
    else endRef = Date.now();

    // Période 1 en cours : si mt1Ms pas encore enregistré, calculer le live
    if (period === 1 && status === "playing") {
      mt1Ms = Math.max(0, endRef - startMs - totalPaused);
    }
    // Période 2 en cours : durée live depuis period_2_started_at
    if (period === 2 && status === "playing" && session.period_2_started_at) {
      mt2Ms = Math.max(0, Date.now() - new Date(session.period_2_started_at).getTime());
    }

    // Label principal = chrono de la mi-temps en cours
    let labelMs = 0;
    if (period === 1) labelMs = mt1Ms || 0;
    else if (period === 2) labelMs = mt2Ms || 0;
    else labelMs = Math.max(0, endRef - startMs - totalPaused);

    const totalSec = Math.floor(labelMs / 1000);
    const fmt = (ms) => {
      const s = Math.floor((ms || 0) / 1000);
      return `${pad(Math.floor(s / 60))}:${pad(s % 60)}`;
    };

    return {
      label: `${pad(Math.floor(totalSec / 60))}:${pad(totalSec % 60)}`,
      running: status === "playing",
      mt1: mt1Ms != null ? fmt(mt1Ms) : null,
      mt2: mt2Ms != null ? fmt(mt2Ms) : null,
      period,
      status,
    };
  }, [session]); // eslint-disable-line
}

// ---------------------------------------------------------------
// PARTNERS MARQUEE (footer)
// ---------------------------------------------------------------
function PartnersMarquee({ partners }) {
  if (!partners?.length) return null;
  const list = [...partners, ...partners];
  return (
    <div className="overflow-hidden bg-white/95 border-y border-white/20 py-2.5 sm:py-3">
      <div className="flex gap-8 sm:gap-10 perfs-marquee-track">
        {list.map((p, i) => (
          <div key={i} className="flex-shrink-0 flex items-center justify-center h-10 sm:h-12 px-3 sm:px-4">
            {p.logo_url ? (
              <img src={p.logo_url} alt={p.name} className="h-9 sm:h-11 w-auto object-contain" />
            ) : (
              <span className="text-slate-700 text-sm uppercase tracking-wider font-bold">{p.name}</span>
            )}
          </div>
        ))}
      </div>
      <style>{`
        @keyframes iphbScroll { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .perfs-marquee-track { width: max-content; animation: iphbScroll 120s linear infinite; will-change: transform; }
        @media (min-width: 640px) { .perfs-marquee-track { animation-duration: 170s; } }
        @media (min-width: 1024px) { .perfs-marquee-track { animation-duration: 220s; } }
      `}</style>
    </div>
  );
}

// ---------------------------------------------------------------
// GOAL ANIMATION
// ---------------------------------------------------------------
function GoalAnimation({ event, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 4500); return () => clearTimeout(t); }, [onDone]);
  const isHome = event.side === "home";
  const playerName = event.player_name || "BUT";
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.6 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 1.2 }}
      transition={{ type: "spring", stiffness: 220, damping: 18 }}
      className="absolute inset-0 z-50 flex flex-col items-center justify-center p-8"
      style={{ background: `radial-gradient(circle at center, ${SECONDARY}cc 0%, ${PRIMARY}f0 50%, ${BG_DEEP}f8 100%)` }}
      data-testid="perfs-goal-animation"
    >
      <motion.div initial={{ y: -50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 }} className="text-center">
        <div className="font-blackhawk uppercase italic tracking-tight leading-none"
          style={{ fontSize: "clamp(8rem, 28vw, 22rem)", color: "#fff", textShadow: `0 0 40px ${SECONDARY}, 0 0 80px ${SECONDARY}`, WebkitTextStroke: `2px ${PRIMARY}` }}>BUT !</div>
        <motion.div initial={{ scale: 0.8 }} animate={{ scale: [0.8, 1.1, 1] }} transition={{ delay: 0.3, duration: 0.6 }}
          className="mt-6 inline-block px-8 py-4 rounded-2xl"
          style={{ background: "rgba(0,0,0,0.55)", border: "2px solid rgba(255,255,255,0.3)" }}>
          <div className="text-white/70 text-xs uppercase tracking-[0.3em] mb-1">{isHome ? "IPHB" : "Adversaire"}</div>
          <div className="text-white font-blackhawk uppercase italic" style={{ fontSize: "clamp(2rem, 6vw, 4rem)" }}>{playerName}</div>
        </motion.div>
      </motion.div>
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {Array.from({ length: 40 }).map((_, i) => (
          <motion.span key={i}
            initial={{ y: -20, x: `${Math.random() * 100}%`, opacity: 1 }}
            animate={{ y: "120vh", rotate: 720 }}
            transition={{ duration: 2 + Math.random() * 2, delay: Math.random() * 1.5, ease: "easeIn" }}
            className="absolute block w-2 h-3 rounded-sm"
            style={{ background: i % 2 ? SECONDARY : "#fff" }} />
        ))}
      </div>
    </motion.div>
  );
}

// ---------------------------------------------------------------
// COMMON BACKGROUND (taureau watermark)
// ---------------------------------------------------------------
function BullWatermark() {
  return (
    <div className="absolute inset-0 pointer-events-none flex items-center justify-center opacity-[0.06]">
      <img src={IPHB_LOGO} alt=""
        className="object-contain"
        style={{ width: "min(90vw, 90vh)", height: "min(90vw, 90vh)",
          filter: "brightness(0) invert(1) drop-shadow(0 0 40px rgba(255,255,255,0.4))" }}
        onError={(e) => { e.currentTarget.style.display = "none"; }} />
    </div>
  );
}

function LiveDot({ label = "Live · Perfs Wall" }) {
  return (
    <div className="flex items-center gap-2">
      <span className="relative flex h-3 w-3">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: SECONDARY }}></span>
        <span className="relative inline-flex rounded-full h-3 w-3" style={{ background: SECONDARY }}></span>
      </span>
      <span className="text-xs uppercase tracking-[0.3em] font-bold">{label}</span>
    </div>
  );
}

// ---------------------------------------------------------------
// MVP WINNER OVERLAY — affiché à la fin du match en plein écran
// par-dessus la LiveScene (status: finished). Lit le résultat du
// sondage MVP via /api/match-live/mvp-winner?session_id={sid}.
// ---------------------------------------------------------------


function MvpWinnerOverlay({ sessionId, players = [], demoSession }) {
  const [winner, setWinner] = useState(null);
  const [show, setShow] = useState(true);

  useEffect(() => {
    if (!sessionId) return;
    if (demoSession) {
      // Démo : faux winner pour la mise en scène
      const sample = (players || []).find((p) => p.last_name) || { first_name: "Alejandro", last_name: "Romero" };
      setWinner({
        player_slug: sample.slug || "demo-mvp",
        player_name: `${sample.first_name || ""} ${sample.last_name || ""}`.trim(),
        votes: 142,
        total_votes: 213,
        percent: 66.7,
        jersey_number: sample.jersey_number,
        photo_url: sample.photo_url,
      });
      return;
    }
    let mounted = true;
    api.get(`/match-live/mvp-winner?session_id=${encodeURIComponent(sessionId)}`)
      .then((r) => {
        if (!mounted) return;
        const w = r.data?.mvp_winner;
        if (w && w.player_name) {
          // Enrichir avec photo / numéro depuis la liste des joueurs
          const p = (players || []).find(
            (x) => x.slug === w.player_slug || (x.last_name && (w.player_name || "").toLowerCase().includes(x.last_name.toLowerCase()))
          );
          setWinner({ ...w, photo_url: p?.photo_url, jersey_number: p?.jersey_number });
        }
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, [sessionId, demoSession, players]);

  if (!winner || !show) return null;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.85 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.8, type: "spring", damping: 18 }}
      className="absolute inset-0 z-[60] flex items-center justify-center pointer-events-none"
      data-testid="perfs-mvp-winner"
    >
      <div
        className="absolute inset-0 backdrop-blur-md"
        style={{ background: `radial-gradient(circle at center, ${SECONDARY}88 0%, ${PRIMARY}cc 50%, ${BG_DEEP}f0 100%)` }}
      />
      {/* Confettis : on positionne via `left:%` (la prop x de framer-motion est en px) */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {Array.from({ length: 60 }).map((_, i) => {
          const leftPct = Math.random() * 100;
          const drift = (Math.random() - 0.5) * 200; // dérive horizontale en px
          return (
            <motion.span
              key={i}
              initial={{ y: -40, x: 0, opacity: 1, rotate: 0 }}
              animate={{ y: "120vh", x: drift, rotate: 720 }}
              transition={{ duration: 3 + Math.random() * 3, delay: Math.random() * 2, ease: "easeIn", repeat: Infinity, repeatDelay: 1.5 }}
              className="absolute block w-2 h-3 rounded-sm"
              style={{
                left: `${leftPct}%`,
                top: 0,
                background: i % 3 === 0 ? "#fff" : i % 3 === 1 ? SECONDARY : "#FFD600",
              }}
            />
          );
        })}
      </div>
      <div className="relative z-10 max-w-3xl w-full px-6 sm:px-8 text-center text-white pointer-events-auto">
        <motion.div
          initial={{ y: -40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-5"
          style={{ background: "rgba(0,0,0,0.45)", border: "1px solid rgba(255,255,255,0.18)" }}
        >
          <Trophy size={18} className="text-yellow-300"/>
          <span className="text-[11px] sm:text-xs uppercase tracking-[0.4em] font-bold text-yellow-200">
            MVP du match · Vote du public
          </span>
        </motion.div>

        {/* Photo agrandie + tête non coupée (object-top) */}
        {(winner.photo_url || winner.jersey_number != null) && (
          <motion.div
            initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.5, type: "spring" }}
            className="inline-flex items-center justify-center mb-5 relative"
          >
            {winner.photo_url ? (
              <img
                src={winner.photo_url}
                alt={winner.player_name}
                className="w-56 h-56 sm:w-72 sm:h-72 md:w-80 md:h-80 rounded-full ring-4 ring-yellow-300 shadow-2xl"
                style={{ objectFit: "cover", objectPosition: "center 18%" }}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <div className="w-56 h-56 sm:w-72 sm:h-72 md:w-80 md:h-80 rounded-full bg-white/15 flex items-center justify-center font-blackhawk text-7xl italic ring-4 ring-yellow-300">
                {(winner.player_name || "?").charAt(0).toUpperCase()}
              </div>
            )}
            {winner.jersey_number != null && (
              <span className="absolute -bottom-2 -right-2 w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-yellow-300 text-[#160732] font-bebas font-black tabular-nums flex items-center justify-center text-3xl shadow-xl">
                {winner.jersey_number}
              </span>
            )}
          </motion.div>
        )}

        {/* Nom plus petit pour laisser la photo dominer */}
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.7 }}
        >
          <div className="font-blackhawk uppercase italic tracking-tight leading-none mb-5"
            style={{ fontSize: "clamp(1.75rem, 5.5vw, 4rem)", color: "#fff",
              textShadow: `0 0 24px ${SECONDARY}, 0 0 48px ${SECONDARY}`, WebkitTextStroke: `1px ${PRIMARY}` }}
            data-testid="perfs-mvp-name">
            {winner.player_name}
          </div>
        </motion.div>

        <motion.div
          initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.9 }}
          className="grid grid-cols-3 gap-3 sm:gap-4 max-w-md mx-auto mb-6"
        >
          <div className="rounded-xl px-3 py-3" style={{ background: "rgba(0,0,0,0.45)", border: "1px solid rgba(255,255,255,0.15)" }}>
            <div className="text-yellow-300 font-bebas font-black tabular-nums leading-none text-3xl sm:text-4xl" data-testid="perfs-mvp-percent">
              {winner.percent}%
            </div>
            <div className="text-white/65 text-[9px] uppercase tracking-wider mt-1">Suffrages</div>
          </div>
          <div className="rounded-xl px-3 py-3" style={{ background: "rgba(0,0,0,0.45)", border: "1px solid rgba(255,255,255,0.15)" }}>
            <div className="text-white font-bebas font-black tabular-nums leading-none text-3xl sm:text-4xl">
              {winner.votes}
            </div>
            <div className="text-white/65 text-[9px] uppercase tracking-wider mt-1">Votes reçus</div>
          </div>
          <div className="rounded-xl px-3 py-3" style={{ background: "rgba(0,0,0,0.45)", border: "1px solid rgba(255,255,255,0.15)" }}>
            <div className="text-white/80 font-bebas font-black tabular-nums leading-none text-3xl sm:text-4xl">
              {winner.total_votes}
            </div>
            <div className="text-white/65 text-[9px] uppercase tracking-wider mt-1">Total</div>
          </div>
        </motion.div>

        <button
          type="button"
          onClick={() => setShow(false)}
          className="text-white/40 hover:text-white/80 text-[10px] uppercase tracking-widest font-bold transition"
          data-testid="perfs-mvp-dismiss"
        >
          Masquer le bandeau
        </button>
      </div>
    </motion.div>
  );
}

// ---------------------------------------------------------------
// PRE-MATCH SCENE — countdown + player carousel
// ---------------------------------------------------------------
function PlayerCard({ player, index, compact = false, side = "home", ribbonLabel, onClick }) {
  const photo = player.photo_url;
  const number = player.jersey_number ?? "—";
  const name = (player.name || `${player.first_name || ""} ${player.last_name || ""}`).trim() || "Joueur";
  const position = player.position || "Joueur de champ";
  const flagEmoji = player.country_flag_emoji || "";
  const ccode = player.country_code || "";
  const sideAccent = side === "away" ? "#ff7eb6" : SECONDARY;
  const bgGrad = side === "away"
    ? `linear-gradient(180deg, #1a0820 0%, ${BG_DEEP} 100%)`
    : `linear-gradient(180deg, ${PRIMARY} 0%, ${BG_DEEP} 100%)`;

  return (
    <motion.div
      key={`pc-${index}-${player.slug || player.id}-${side}`}
      initial={{ opacity: 0, scale: 0.92, y: 18 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96, y: -18 }}
      transition={{ duration: 0.5, ease: [0.21, 0.94, 0.4, 1] }}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") onClick(); } : undefined}
      className={`group relative w-full rounded-2xl overflow-hidden shadow-2xl ${onClick ? "cursor-pointer hover:scale-[1.02] active:scale-[0.99]" : ""}`}
      style={{
        background: bgGrad,
        border: "1px solid rgba(255,255,255,0.1)",
        boxShadow: `0 18px 40px rgba(0,0,0,0.55), 0 0 28px ${sideAccent}44`,
      }}
      data-testid={`perfs-player-card-${side}-${index}`}
    >
      {/* Aspect ratio 3:4 (portrait, comme le site) */}
      <div className="relative pb-[133%] overflow-hidden">
        {/* Decorative diagonal stripes */}
        <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
          background: `repeating-linear-gradient(135deg, transparent 0 14px, rgba(255,255,255,0.04) 14px 16px)`,
        }}/>

        {/* HUGE jersey number derrière la photo (rotated, semi-transparent) */}
        {number !== "—" && (
          <div className="absolute top-2 right-3 select-none pointer-events-none z-0">
            <div
              className="font-bebas tabular-nums leading-none"
              style={{
                fontSize: compact ? "clamp(5rem, 22vw, 8rem)" : "clamp(7rem, 16vw, 12rem)",
                color: "rgba(255,255,255,0.18)",
                transform: "rotate(-8deg)",
                fontWeight: 900,
                textShadow: `0 0 40px ${sideAccent}55`,
              }}
            >
              {number}
            </div>
          </div>
        )}

        {/* Photo plein cadre */}
        {photo ? (
          <img
            src={photo}
            alt={name}
            className="absolute inset-0 w-full h-full object-cover object-top z-[1] transform-gpu group-hover:scale-[1.04] transition duration-500"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center z-[1]">
            <div className="text-white/25 font-bebas font-black tracking-widest text-center"
              style={{ fontSize: compact ? "clamp(3rem, 14vw, 5rem)" : "clamp(5rem, 14vw, 8rem)" }}>
              {(name || "?").split(" ").map(s => s[0]).join("").slice(0, 2).toUpperCase()}
            </div>
          </div>
        )}

        {/* Ribbon team label (haut gauche) */}
        <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full font-bold uppercase tracking-[0.2em] z-[2]"
          style={{
            background: sideAccent, color: "#fff",
            fontSize: compact ? "9px" : "10px",
            boxShadow: `0 4px 12px ${sideAccent}55`,
          }}>
          {ribbonLabel || (side === "away" ? "Adv." : "IPHB")}
        </div>

        {/* Pas de drapeau en haut — il chevauche le visage du joueur.
            Le petit drapeau devant le nom (bandeau bas) est suffisant. */}

        {/* Bottom overlay : name + position */}
        <div className="absolute inset-x-0 bottom-0 z-[2]"
          style={{ background: `linear-gradient(0deg, rgba(5,0,16,0.95) 0%, rgba(5,0,16,0.85) 60%, transparent 100%)` }}>
          <div className={`text-center ${compact ? "px-2 pt-7 pb-3" : "px-3 pt-10 pb-4"}`}>
            <div
              className="font-blackhawk italic uppercase text-white tracking-tight flex items-center justify-center gap-1.5 flex-wrap"
              style={{
                fontSize: compact ? "clamp(1.15rem, 5.5vw, 1.65rem)" : "clamp(1.5rem, 3.6vw, 2.1rem)",
                lineHeight: 1.05,
                wordBreak: "break-word",
                hyphens: "auto",
              }}
            >
              {(ccode || flagEmoji) && (
                <span className="inline-flex shrink-0 rounded-sm overflow-hidden ring-1 ring-white/40 align-middle">
                  <Flag code={ccode} emoji={flagEmoji} size={compact ? 16 : 22} label={player.country || ""}/>
                </span>
              )}
              <span className="block">{name}</span>
            </div>
            <div className="font-bold tracking-[0.25em] uppercase mt-1.5"
              style={{ color: sideAccent, fontSize: compact ? "11px" : "11px" }}>
              {position}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function EmptyAwayCard({ teamName, logo, compact = false }) {
  return (
    <div
      className={`relative w-full ${compact ? "" : "max-w-md mx-auto"} rounded-2xl overflow-hidden flex flex-col items-center justify-center text-center`}
      style={{
        background: `linear-gradient(160deg, #1a0820 0%, ${BG_DEEP} 70%)`,
        boxShadow: `0 14px 30px rgba(0,0,0,0.5), 0 0 22px rgba(255,255,255,0.08)`,
        border: "1px dashed rgba(255,255,255,0.18)",
        minHeight: compact ? "12rem" : "16rem",
        padding: compact ? "1rem" : "1.5rem",
      }}
      data-testid="perfs-empty-away-card"
    >
      {logo && (
        <img src={logo} alt={teamName} className={`${compact ? "h-14 w-14" : "h-20 w-20"} object-contain mb-2 opacity-90`}
          onError={(e) => { e.currentTarget.style.display = "none"; }}/>
      )}
      <div className={`uppercase tracking-[0.25em] font-bold mb-1 text-white/60 ${compact ? "text-[8px]" : "text-[10px]"}`}>Équipe adverse</div>
      <div className={`font-blackhawk uppercase italic text-white leading-tight ${compact ? "text-sm" : "text-lg"}`}>{teamName}</div>
      <div className={`mt-2 text-white/40 italic ${compact ? "text-[10px]" : "text-xs"}`}>Effectif à venir</div>
    </div>
  );
}

function PreMatchScene({ match, kickoffIso, partners, players, awayPlayers, standing, oppStanding, onOpenRoster, onOpenRosterSide, onOpenSearch, onOpenPlayer }) {
  const [idx, setIdx] = useState(0);
  const proPlayers = useMemo(
    () => (players || []).filter((p) => (p.group || "").toUpperCase().startsWith("PRO") && p.last_name)
                          .sort((a, b) => (a.jersey_number || 99) - (b.jersey_number || 99)),
    [players]
  );
  const oppPlayers = useMemo(
    () => (awayPlayers || []).filter((p) => (p.last_name || p.name)).slice(0, 30)
                              .sort((a, b) => (a.jersey_number || 99) - (b.jersey_number || 99)),
    [awayPlayers]
  );
  const maxLen = Math.max(proPlayers.length, oppPlayers.length, 1);

  // Both columns advance together every 4.5 s
  useEffect(() => {
    if (maxLen <= 1) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % maxLen), 4500);
    return () => clearInterval(id);
  }, [maxLen]);

  const cd = useMemo(() => {
    if (!kickoffIso) return null;
    const t = new Date(kickoffIso).getTime();
    return { expired: !isNaN(t) && t - Date.now() <= 0 };
  }, [kickoffIso]);
  useTick(1000);

  const homePlayer = proPlayers[idx % (proPlayers.length || 1)];
  const oppPlayer = oppPlayers.length ? oppPlayers[idx % oppPlayers.length] : null;
  const homeName = match?.home_team || "Istres Provence HB";
  const awayName = match?.away_team || "Adversaire";
  const homeLogo = match?.home_logo;
  const awayLogo = match?.away_logo;
  const isHomeMatch = /istres|iphb/i.test(homeName);

  // PERFS WALL = écran du club IPHB → on affiche TOUJOURS IPHB à gauche, adversaire à droite,
  // peu importe que le match soit à domicile ou à l'extérieur.
  const iphbName = isHomeMatch ? homeName : awayName;
  const iphbLogo = isHomeMatch ? homeLogo : awayLogo;
  const oppName = isHomeMatch ? awayName : homeName;
  const oppLogo = isHomeMatch ? awayLogo : homeLogo;

  return (
    <div className="relative z-10 flex flex-col min-h-screen">
      {/* Partners marquee — placée tout en haut */}
      <PartnersMarquee partners={partners} />

      {/* League badges row : LNH + Liqui Moly + classement + venue + TV */}
      <div className="px-3 sm:px-4 pt-2 sm:pt-3 flex items-center justify-center flex-wrap gap-1.5 sm:gap-3">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/95 border border-white/30 shadow-md">
          <LnhBadge size="sm"/>
          <span className="text-slate-300 text-xs">·</span>
          <LiquiMolyBadge size="sm"/>
        </div>
        {standing && (
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white text-[10px] sm:text-xs uppercase tracking-wider font-bold"
            style={{ background: `linear-gradient(135deg, ${SECONDARY}33, ${PRIMARY}33)`, border: `1px solid ${SECONDARY}55` }}
            data-testid="perfs-standing-chip">
            <span style={{ color: "#fff" }}>{standing.rank}<sup>e</sup>{standing.total ? ` / ${standing.total}` : ""}</span>
            <span className="text-white/60">·</span>
            <span>{standing.points} pts</span>
            <span className="text-white/40 hidden sm:inline">({standing.wins}V · {standing.draws}N · {standing.losses}D)</span>
          </div>
        )}
        {match?.venue && (
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-white text-[10px] sm:text-xs uppercase tracking-wider font-bold">
            <MapPin size={12} className="text-pink-300"/>
            <span className="truncate max-w-[20ch] sm:max-w-none">{match.venue}</span>
          </div>
        )}
        {match?.tv && (
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-white text-[11px] sm:text-sm uppercase tracking-wider font-bold"
            style={{ background: "linear-gradient(135deg, rgba(236,31,124,0.25), rgba(79,27,124,0.25))", border: "1px solid rgba(236,31,124,0.4)" }}>
            <span className="text-base">📺</span>
            {match.tv_logo ? <img src={match.tv_logo} alt={match.tv} className="h-7 sm:h-8 w-auto object-contain"/> : <span>{match.tv}</span>}
          </div>
        )}
      </div>

      {/* Match teaser + countdown + actions + carousel — desktop 3-col split */}
      <div className="lg:grid lg:grid-cols-[1fr,1.6fr,1fr] lg:gap-6 lg:max-w-7xl lg:mx-auto lg:w-full lg:px-6 lg:py-4">
        {/* DESKTOP LEFT : IPHB toujours à gauche */}
        <div className="hidden lg:flex flex-col gap-4 self-start">
          <BigTeamPanel name={iphbName} logo={iphbLogo} side="home" label={isHomeMatch ? "À domicile" : "À l'extérieur"} standing={standing} onOpenRoster={() => onOpenRosterSide?.("home")}/>
          <div className="w-full max-w-[220px] mx-auto">
            <AnimatePresence mode="wait" initial={false}>
              {homePlayer && (
                <PlayerCard key={`d-home-${idx}`} player={homePlayer} index={idx} side="home" ribbonLabel={(iphbName||"IPHB").slice(0,4).toUpperCase()} onClick={() => onOpenPlayer?.(homePlayer.slug)}/>
              )}
            </AnimatePresence>
            {!homePlayer && (
              <div className="rounded-2xl p-8 text-center text-white/40 italic text-sm" style={{background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)"}}>Chargement de l'effectif…</div>
            )}
          </div>
        </div>

        {/* CENTER (mobile + desktop) : teaser + countdown + actions + presenter + 2-col carousel */}
        <div className="lg:flex lg:flex-col">

      {/* Match teaser */}
      <div className="px-3 pt-3 pb-3 flex-shrink-0">
        <div className="text-center">
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.4em] text-pink-300 font-bold mb-2">
            {match?.matchday || match?.round ? `Journée ${match?.round || ""}` : "Prochain match"} · {match?.league || "Liqui Moly StarLigue"}
          </div>
          <div className="grid grid-cols-[1fr,auto,1fr] items-center gap-2 sm:gap-3 max-w-md mx-auto lg:hidden">
            <div className="flex flex-col items-center min-w-0">
              {iphbLogo ? <img src={iphbLogo} alt={iphbName} className="h-20 w-20 sm:h-24 sm:w-24 object-contain mb-1.5 drop-shadow-[0_8px_12px_rgba(0,0,0,0.5)]" onError={(e)=>{e.currentTarget.style.display="none";}}/> : null}
              <div className="font-blackhawk italic uppercase text-white leading-tight text-center truncate w-full" data-testid="perfs-prematch-home"
                style={{ fontSize: "clamp(0.95rem, 4.2vw, 1.3rem)" }}>{iphbName}</div>
              <div className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-pink-300 font-bold mt-1">{isHomeMatch ? "Domicile" : "Extérieur"}</div>
              {standing?.rank && (
                <div className="mt-1.5 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-white text-[10px] sm:text-[11px] font-bold uppercase tracking-wider"
                  style={{ background: `linear-gradient(135deg, ${SECONDARY}55, transparent)`, border: `1px solid ${SECONDARY}88` }}
                  data-testid="perfs-home-rank-mobile">
                  <span className="font-bebas font-black tabular-nums" style={{ fontSize: "1.15rem", color: "#fff", lineHeight: 1 }}>{standing.rank}<sup className="text-[0.55em]">e</sup></span>
                  <span className="text-white/70">/</span>
                  <span className="text-white/85">{standing.total || "—"}</span>
                  <span className="text-white/40">·</span>
                  <span>{standing.points} pts</span>
                </div>
              )}
              <button
                onClick={() => onOpenRosterSide?.("home")}
                data-testid="perfs-open-roster-home-mobile"
                className="mt-2 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[12px] font-black uppercase tracking-[0.18em] transition active:scale-95 shadow-lg"
                style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)`, color: "#fff", boxShadow: `0 6px 14px ${SECONDARY}66` }}
              >
                <Users size={13}/> Voir l'effectif
              </button>
            </div>
            <span className="font-blackhawk italic tracking-widest leading-none self-start mt-6"
              style={{ fontSize: "clamp(2.6rem, 11vw, 5rem)", color: SECONDARY,
                textShadow: `0 0 22px ${SECONDARY}, 0 0 50px ${SECONDARY}55` }}>VS</span>
            <div className="flex flex-col items-center min-w-0">
              {oppLogo ? <img src={oppLogo} alt={oppName} className="h-20 w-20 sm:h-24 sm:w-24 object-contain mb-1.5 drop-shadow-[0_8px_12px_rgba(0,0,0,0.5)]" onError={(e)=>{e.currentTarget.style.display="none";}}/> : null}
              <div className="font-blackhawk italic uppercase text-white leading-tight text-center truncate w-full" data-testid="perfs-prematch-away"
                style={{ fontSize: "clamp(0.95rem, 4.2vw, 1.3rem)" }}>{oppName}</div>
              <div className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-white/55 font-bold mt-1">Adversaire</div>
              {oppStanding?.rank && (
                <div className="mt-1.5 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-white text-[10px] sm:text-[11px] font-bold uppercase tracking-wider"
                  style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.25)" }}
                  data-testid="perfs-away-rank-mobile">
                  <span className="font-bebas font-black tabular-nums" style={{ fontSize: "1.15rem", color: "#fff", lineHeight: 1 }}>{oppStanding.rank}<sup className="text-[0.55em]">e</sup></span>
                  <span className="text-white/70">/</span>
                  <span className="text-white/85">{oppStanding.total || "—"}</span>
                  <span className="text-white/40">·</span>
                  <span>{oppStanding.points} pts</span>
                </div>
              )}
              <button
                onClick={() => onOpenRosterSide?.("away")}
                data-testid="perfs-open-roster-away-mobile"
                className="mt-2 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[12px] font-black uppercase tracking-[0.18em] transition active:scale-95 shadow-lg"
                style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.18), rgba(255,255,255,0.08))", color: "#fff", border: "1.5px solid rgba(255,255,255,0.35)" }}
              >
                <Users size={13}/> Voir l'effectif
              </button>
            </div>
          </div>
          {match?.venue && <div className="mt-2 text-white/65 text-[11px] sm:text-xs uppercase tracking-widest truncate">{match.venue}</div>}
        </div>
      </div>

      {/* Countdown — split-flap identique au site */}
      <div className="px-3 py-3">
        <div className="text-center text-[11px] sm:text-xs uppercase tracking-[0.4em] text-white/70 font-bold mb-2.5">
          {cd?.expired ? "Coup d'envoi imminent !" : "Coup d'envoi dans"}
        </div>
        <div className="flex justify-center" data-testid="perfs-countdown">
          {kickoffIso ? <MatchCountdown target={kickoffIso} accentColor="#fff" liveLabel="Coup d'envoi !" /> :
            <div className="text-white/50 italic text-sm">Heure de coup d'envoi inconnue</div>}
        </div>
      </div>

      {/* Quick actions : recherche fiche joueur (les boutons "Voir l'effectif" sont sous chaque logo) */}
      <div className="px-3 pb-2 flex items-center justify-center gap-2 flex-wrap">
        <button
          onClick={() => onOpenSearch?.()}
          data-testid="perfs-open-search"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 text-white text-[10px] sm:text-[11px] font-black uppercase tracking-widest transition"
        >
          <Search size={12}/> Rechercher un joueur
        </button>
      </div>

      {/* Présentateur du match (uniquement à domicile) + sponsor présentateur si défini */}
      <div className="px-3 pb-2 flex items-center justify-center gap-3 flex-wrap">
        {isHomeMatch && (
          <PresenterCard photo={PRESENTER_PHOTO} name={PRESENTER_NAME}/>
        )}
        {match?.match_sponsor?.name && (
          <SponsorPresenterCard sponsor={match.match_sponsor}/>
        )}
      </div>

      {/* Player carousel — 2 colonnes (HOME + AWAY) qui défilent en simultané (mobile uniquement) */}
      <div className="lg:hidden flex-1 flex flex-col items-center justify-center px-3 py-3 min-h-[16rem]">
        <div className="text-center text-[9px] sm:text-[10px] uppercase tracking-[0.4em] text-white/50 font-bold mb-2">
          Effectifs · cycle automatique
        </div>
        <div className="grid grid-cols-2 gap-2 sm:gap-3 w-full max-w-2xl mx-auto" data-testid="perfs-double-carousel">
          <AnimatePresence mode="wait" initial={false}>
            {homePlayer ? (
              <PlayerCard key={`home-${idx}`} player={homePlayer} index={idx} compact side="home" ribbonLabel={(iphbName||"").slice(0,4).toUpperCase()} onClick={() => onOpenPlayer?.(homePlayer.slug)}/>
            ) : (
              <div key="home-loading" className="text-center text-white/40 italic text-xs py-8">Chargement…</div>
            )}
          </AnimatePresence>
          <AnimatePresence mode="wait" initial={false}>
            {oppPlayer ? (
              <PlayerCard key={`away-${idx}`} player={oppPlayer} index={idx} compact side="away" ribbonLabel={(oppName||"").slice(0,4).toUpperCase()} onClick={() => onOpenPlayer?.(oppPlayer.slug)}/>
            ) : (
              <EmptyAwayCard key="away-empty" teamName={oppName} logo={oppLogo} compact />
            )}
          </AnimatePresence>
        </div>
        {maxLen > 1 && (
          <div className="mt-3 flex justify-center gap-1.5">
            {Array.from({ length: Math.min(maxLen, 16) }).map((_, i) => (
              <span key={i} className="block h-1 rounded-full transition-all"
                style={{ width: i === (idx % 16) ? 16 : 5, background: i === (idx % 16) ? SECONDARY : "rgba(255,255,255,0.2)" }}/>
            ))}
          </div>
        )}
      </div>
        </div>{/* /center */}

        {/* DESKTOP RIGHT : Adversaire toujours à droite */}
        <div className="hidden lg:flex flex-col gap-4 self-start">
          <BigTeamPanel name={oppName} logo={oppLogo} side="away" label={isHomeMatch ? "Adversaire (visiteur)" : "Adversaire (à domicile)"} standing={oppStanding} onOpenRoster={() => onOpenRosterSide?.("away")}/>
          <div className="w-full max-w-[220px] mx-auto">
            <AnimatePresence mode="wait" initial={false}>
              {oppPlayer && (
                <PlayerCard key={`d-away-${idx}`} player={oppPlayer} index={idx} side="away" ribbonLabel={(oppName||"ADV").slice(0,4).toUpperCase()} onClick={() => onOpenPlayer?.(oppPlayer.slug)}/>
              )}
            </AnimatePresence>
            {!oppPlayer && (
              <EmptyAwayCard teamName={oppName} logo={oppLogo}/>
            )}
          </div>
        </div>
      </div>{/* /lg-grid */}

      <div className="mt-auto pt-4">
        <div className="text-center text-[10px] text-white/40 py-2 uppercase tracking-[0.3em]">
          Suivez le match · #IPHB
        </div>
        <MscFooterCredit/>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
// LIVE SCENE
// ---------------------------------------------------------------
function LiveScene({ session, partners, players, awayPlayers, goalEvent, onGoalDone, onOpenPlayer, onOpenRosterSide }) {
  const clock = useMatchClock(session);
  const homeName = session?.home_team || "IPHB";
  const awayName = session?.away_team || "—";
  const homeLogo = session?.home_logo;
  const awayLogo = session?.away_logo;
  const homeScore = session?.home_score ?? 0;
  const awayScore = session?.away_score ?? 0;
  const period = session?.period;
  const periodLabel = session?.status === "halftime" ? "MI-TEMPS" : period === 2 ? "2ᵉ MI-TEMPS" : period === 1 ? "1ʳᵉ MI-TEMPS" : "—";
  const isHomeMatch = /istres|iphb/i.test(homeName);

  // Rotating players (same logic as PreMatchScene)
  const [idx, setIdx] = useState(0);
  const proPlayers = useMemo(
    () => (players || []).filter((p) => (p.group || "").toUpperCase().startsWith("PRO") && p.last_name)
                          .sort((a, b) => (a.jersey_number || 99) - (b.jersey_number || 99)),
    [players]
  );
  const oppPlayers = useMemo(
    () => (awayPlayers || []).filter((p) => (p.last_name || p.name)).slice(0, 30)
                              .sort((a, b) => (a.jersey_number || 99) - (b.jersey_number || 99)),
    [awayPlayers]
  );
  const maxLen = Math.max(proPlayers.length, oppPlayers.length, 1);
  useEffect(() => {
    if (maxLen <= 1) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % maxLen), 4500);
    return () => clearInterval(id);
  }, [maxLen]);
  const homePlayer = proPlayers.length ? proPlayers[idx % proPlayers.length] : null;
  const oppPlayer = oppPlayers.length ? oppPlayers[idx % oppPlayers.length] : null;

  // Display IPHB always on the left regardless of home/away match
  const leftName = isHomeMatch ? homeName : awayName;
  const leftLogo = isHomeMatch ? homeLogo : awayLogo;
  const leftScore = isHomeMatch ? homeScore : awayScore;
  const rightName = isHomeMatch ? awayName : homeName;
  const rightLogo = isHomeMatch ? awayLogo : homeLogo;
  const rightScore = isHomeMatch ? awayScore : homeScore;

  // === Stats live IPHB calculées depuis le feed d'événements ===
  const liveStats = useMemo(() => {
    const evs = session?.events || [];
    const iphbSide = isHomeMatch ? "home" : "away";
    const oppSide = isHomeMatch ? "away" : "home";
    const iphbGoals = evs.filter((e) => e.kind === "goal" && e.side === iphbSide).length;
    const iphbMiss = evs.filter((e) => e.kind === "shot_miss" && e.side === iphbSide).length;
    const savedByOpp = evs.filter((e) => e.kind === "gk_save" && e.side === oppSide).length;
    const iphbShots = iphbGoals + iphbMiss + savedByOpp;
    const efficiency = iphbShots > 0 ? Math.round((iphbGoals / iphbShots) * 100) : null;
    const iphbSaves = evs.filter((e) => e.kind === "gk_save" && e.side === iphbSide).length;
    // Top gardien IPHB par nombre d'arrêts
    const gkMap = {};
    evs.filter((e) => e.kind === "gk_save" && e.side === iphbSide && e.player_name).forEach((e) => {
      gkMap[e.player_name] = (gkMap[e.player_name] || 0) + 1;
    });
    const topGk = Object.entries(gkMap).sort((a, b) => b[1] - a[1])[0] || null;
    return { iphbGoals, iphbShots, efficiency, iphbSaves, topGk };
  }, [session, isHomeMatch]);

  return (
    <div className="relative z-10 flex flex-col min-h-screen">
      {/* Top header strip — caché sur mobile pour gagner de la place, gardé sur PC */}
      <div className="hidden lg:flex items-center justify-between px-6 pt-6 pb-3">
        <LiveDot/>
        <div className="text-xs uppercase tracking-[0.2em] text-white/60 font-bold">#IPHB</div>
      </div>

      {/* Partners marquee */}
      <PartnersMarquee partners={partners} />

      {/* Period + clock */}
      <div className="flex flex-col items-center px-4 pt-1 lg:pt-4">
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-1" data-testid="perfs-period">{periodLabel}</div>
        <div className="px-5 py-2 rounded-2xl mb-2"
          style={{ background: "rgba(0,0,0,0.45)", border: "1px solid rgba(255,255,255,0.15)",
            boxShadow: `inset 0 2px 12px rgba(0,0,0,0.5), 0 0 30px ${SECONDARY}33` }}
          data-testid="perfs-clock">
          <div className="font-bebas font-black tabular-nums tracking-wider"
            style={{ fontSize: "clamp(2.4rem, 10vw, 5rem)", color: clock.running ? "#fff" : "#ff9eb1" }}>
            {clock.label}
          </div>
        </div>
        <div className="flex items-stretch gap-2 sm:gap-3 mb-3 lg:mb-4" data-testid="perfs-period-clocks">
          <div className={`px-3 py-2 rounded-xl text-center min-w-[5.5rem] ${period === 1 && session?.status === "playing" ? "ring-2" : "border border-white/10"}`}
            style={{
              background: period === 1 ? `linear-gradient(135deg, ${SECONDARY}33, ${PRIMARY}55)` : "rgba(255,255,255,0.05)",
              boxShadow: period === 1 && session?.status === "playing" ? `0 0 20px ${SECONDARY}66` : "none",
              ...(period === 1 && session?.status === "playing" ? { borderColor: SECONDARY } : {}),
            }}>
            <div className="text-[8px] uppercase tracking-[0.3em] font-bold mb-0.5"
              style={{ color: period === 1 ? "#fff" : "rgba(255,255,255,0.45)" }}>1ʳᵉ mi-temps</div>
            <div className="font-black tabular-nums leading-none" style={{ fontSize: "clamp(1.1rem, 4.5vw, 1.6rem)", color: period === 1 ? "#fff" : "rgba(255,255,255,0.55)" }}
              data-testid="perfs-mt1-clock">
              {clock.mt1 || "--:--"}
            </div>
          </div>
          <div className={`px-3 py-2 rounded-xl text-center min-w-[5.5rem] ${period === 2 && session?.status === "playing" ? "ring-2" : "border border-white/10"}`}
            style={{
              background: period === 2 ? `linear-gradient(135deg, ${SECONDARY}33, ${PRIMARY}55)` : "rgba(255,255,255,0.05)",
              boxShadow: period === 2 && session?.status === "playing" ? `0 0 20px ${SECONDARY}66` : "none",
              ...(period === 2 && session?.status === "playing" ? { borderColor: SECONDARY } : {}),
            }}>
            <div className="text-[8px] uppercase tracking-[0.3em] font-bold mb-0.5"
              style={{ color: period === 2 ? "#fff" : "rgba(255,255,255,0.45)" }}>2ᵉ mi-temps</div>
            <div className="font-black tabular-nums leading-none" style={{ fontSize: "clamp(1.1rem, 4.5vw, 1.6rem)", color: period === 2 ? "#fff" : "rgba(255,255,255,0.55)" }}
              data-testid="perfs-mt2-clock">
              {clock.mt2 || "--:--"}
            </div>
          </div>
        </div>
      </div>

      {/* DESKTOP: 3-column layout — IPHB | scoreboard center | Adversaire */}
      <div className="hidden lg:grid lg:grid-cols-[1fr,auto,1fr] gap-5 px-6 pb-2 items-start">
        {/* LEFT — IPHB */}
        <div className="flex flex-col gap-4 self-start">
          <BigTeamPanel name={leftName} logo={leftLogo} side="home" label={isHomeMatch ? "À domicile" : "À l'extérieur"} onOpenRoster={() => onOpenRosterSide?.("home")}/>
          <div className="w-full max-w-[200px] mx-auto">
            <AnimatePresence mode="wait" initial={false}>
              {homePlayer && (
                <PlayerCard key={`live-d-home-${idx}`} player={homePlayer} index={idx} side="home" ribbonLabel={(leftName||"IPHB").slice(0,4).toUpperCase()} onClick={() => onOpenPlayer?.(homePlayer.slug)}/>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* CENTER — Scoreboard */}
        <div className="flex flex-col items-center pt-6">
          <div className="flex items-center gap-3">
            <motion.div key={`live-h-${leftScore}`} initial={{ scale: 1.5, color: SECONDARY }} animate={{ scale: 1, color: "#fff" }} transition={{ duration: 0.6 }}
              className="font-bebas font-black tabular-nums leading-none" style={{ fontSize: "clamp(5rem, 12vw, 10rem)" }} data-testid="perfs-home-score">{leftScore}</motion.div>
            <div className="text-white/40 font-black" style={{ fontSize: "clamp(3rem, 8vw, 6rem)" }}>:</div>
            <motion.div key={`live-a-${rightScore}`} initial={{ scale: 1.5, color: SECONDARY }} animate={{ scale: 1, color: "#fff" }} transition={{ duration: 0.6 }}
              className="font-bebas font-black tabular-nums leading-none" style={{ fontSize: "clamp(5rem, 12vw, 10rem)" }} data-testid="perfs-away-score">{rightScore}</motion.div>
          </div>
          {/* Events feed — gros et visible */}
          <div className="w-full mt-6 max-w-md">
            {/* Stats Live IPHB — % efficacité + arrêts gardien */}
            <div className="grid grid-cols-3 gap-3 mb-6" data-testid="perfs-stats-live">
              <div className="rounded-xl border-2 px-3 py-3 text-center"
                   style={{ background: `${SECONDARY}14`, borderColor: `${SECONDARY}55` }}>
                <div className="text-[11px] sm:text-xs uppercase tracking-wider font-black text-pink-300/95">Buts / Tirs</div>
                <div className="tabular-nums text-white font-black mt-1" style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)", lineHeight: 1, fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif" }}>
                  {liveStats.iphbGoals}<span className="text-white/45">/{liveStats.iphbShots || 0}</span>
                </div>
              </div>
              <div className="rounded-xl border-2 px-3 py-3 text-center"
                   style={{ background: "rgba(34,211,238,0.10)", borderColor: "rgba(34,211,238,0.45)" }}>
                <div className="text-[11px] sm:text-xs uppercase tracking-wider font-black" style={{ color: "#67E8F9" }}>Efficacité</div>
                <div className="tabular-nums text-white font-black mt-1" style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)", lineHeight: 1, fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif" }}>
                  {liveStats.efficiency === null ? "—" : `${liveStats.efficiency}%`}
                </div>
              </div>
              <div className="rounded-xl border-2 px-3 py-3 text-center"
                   style={{ background: "rgba(34,211,238,0.10)", borderColor: "rgba(34,211,238,0.45)" }}>
                <div className="text-[11px] sm:text-xs uppercase tracking-wider font-black" style={{ color: "#67E8F9" }}>Arrêts GK</div>
                <div className="tabular-nums text-white font-black mt-1" style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)", lineHeight: 1, fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif" }}>
                  {liveStats.iphbSaves}
                </div>
                {liveStats.topGk && (
                  <div className="text-[11px] text-white/70 mt-1 truncate font-bold uppercase tracking-wide">{liveStats.topGk[0]}</div>
                )}
              </div>
            </div>
            <div className="text-pink-400 font-black tracking-[0.3em] text-sm sm:text-base uppercase mb-4 text-center flex items-center justify-center gap-2">
              <span className="h-[2px] flex-1 max-w-[40px] bg-gradient-to-r from-transparent to-pink-400/60"/>
              Faits de match
              <span className="h-[2px] flex-1 max-w-[40px] bg-gradient-to-l from-transparent to-pink-400/60"/>
            </div>
            <div className="space-y-2" data-testid="perfs-events-feed">
              {(session?.events || []).slice(-6).reverse().map((ev) => {
                const tone = ev.kind === "goal" ? SECONDARY
                  : ev.kind === "gk_save" ? "#22D3EE"
                  : ev.kind === "shot_miss" ? "#94A3B8"
                  : ev.kind === "card" || ev.kind === "2min" ? "#fbbf24"
                  : "#fff";
                const label = ev.kind === "goal" ? "BUT"
                  : ev.kind === "halftime" ? "MI-TEMPS"
                  : ev.kind === "resume" ? "REPRISE"
                  : ev.kind === "7m" ? "7 MÈTRES"
                  : ev.kind === "2min" ? "2 MIN"
                  : ev.kind === "card" ? "CARTON"
                  : ev.kind === "timeout" ? "TIMEOUT"
                  : ev.kind === "gk_save" ? "ARRÊT GK"
                  : ev.kind === "shot_miss" ? "TIR MANQUÉ"
                  : (ev.kind || "").toUpperCase();
                return (
                  <div key={ev.id}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-base"
                    style={{
                      background: ev.kind === "goal" ? `linear-gradient(135deg, ${SECONDARY}26, transparent)` : "rgba(255,255,255,0.06)",
                      borderColor: ev.kind === "goal" ? `${SECONDARY}66` : "rgba(255,255,255,0.12)",
                    }}>
                    <span className="font-black uppercase tabular-nums shrink-0"
                      style={{ color: tone, fontSize: "clamp(0.95rem, 1.4vw, 1.15rem)", letterSpacing: "0.05em", fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif" }}>
                      {label}
                    </span>
                    {ev.player_name && <span className="text-white font-bold uppercase truncate flex-1" style={{ fontSize: "clamp(0.9rem, 1.3vw, 1.05rem)" }}>{ev.player_name}</span>}
                    <span className="text-white/55 text-xs ml-auto font-bold uppercase tracking-wider shrink-0">
                      {ev.side === "home" ? "IPHB" : ev.side === "away" ? "ADV." : ""}
                    </span>
                  </div>
                );
              })}
              {!session?.events?.length && <div className="text-center text-white/40 text-sm italic py-6">Aucun fait de match pour le moment</div>}
            </div>
          </div>
        </div>

        {/* RIGHT — Adversaire */}
        <div className="flex flex-col gap-4 self-start">
          <BigTeamPanel name={rightName} logo={rightLogo} side="away" label={isHomeMatch ? "Adversaire (visiteur)" : "Adversaire (à domicile)"} onOpenRoster={() => onOpenRosterSide?.("away")}/>
          <div className="w-full max-w-[200px] mx-auto">
            <AnimatePresence mode="wait" initial={false}>
              {oppPlayer && (
                <PlayerCard key={`live-d-away-${idx}`} player={oppPlayer} index={idx} side="away" ribbonLabel={(rightName||"ADV").slice(0,4).toUpperCase()} onClick={() => onOpenPlayer?.(oppPlayer.slug)}/>
              )}
              {!oppPlayer && (
                <EmptyAwayCard teamName={rightName} logo={rightLogo}/>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* MOBILE layout */}
      <div className="lg:hidden flex flex-col px-3 pb-2">
        {/* Compact teams + score — logos plus gros, collés au top */}
        <div className="grid grid-cols-3 gap-2 items-center mb-2 -mt-2">
          <div className="flex flex-col items-center text-center">
            {leftLogo ? <img src={leftLogo} alt={leftName} className="h-32 w-32 sm:h-36 sm:w-36 object-contain mb-1.5 drop-shadow-[0_8px_18px_rgba(0,0,0,0.6)]" /> :
              <div className="h-32 w-32 rounded-full bg-white/10 mb-1.5 flex items-center justify-center text-3xl font-blackhawk italic">{leftName.slice(0, 3).toUpperCase()}</div>}
            <div className="font-blackhawk italic uppercase text-white leading-tight text-center w-full break-words"
              style={{ fontSize: "clamp(1rem, 4.6vw, 1.4rem)" }}>{leftName}</div>
            <button
              onClick={() => onOpenRosterSide?.("home")}
              data-testid="perfs-open-roster-home-mobile"
              className="mt-2 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[11px] font-black uppercase tracking-[0.18em] transition active:scale-95 shadow-lg"
              style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)`, color: "#fff", boxShadow: `0 6px 14px ${SECONDARY}66` }}>
              <Users size={12}/> Voir l'effectif
            </button>
          </div>
          <div className="flex items-center justify-center gap-1">
            <motion.div key={`live-mh-${leftScore}`} initial={{ scale: 1.5, color: SECONDARY }} animate={{ scale: 1, color: "#fff" }} transition={{ duration: 0.6 }}
              className="font-bebas font-black tabular-nums leading-none" style={{ fontSize: "clamp(2.6rem, 12vw, 5rem)" }}>{leftScore}</motion.div>
            <div className="text-white/40 font-black" style={{ fontSize: "clamp(1.6rem, 7vw, 2.6rem)" }}>:</div>
            <motion.div key={`live-ma-${rightScore}`} initial={{ scale: 1.5, color: SECONDARY }} animate={{ scale: 1, color: "#fff" }} transition={{ duration: 0.6 }}
              className="font-bebas font-black tabular-nums leading-none" style={{ fontSize: "clamp(2.6rem, 12vw, 5rem)" }}>{rightScore}</motion.div>
          </div>
          <div className="flex flex-col items-center text-center">
            {rightLogo ? <img src={rightLogo} alt={rightName} className="h-32 w-32 sm:h-36 sm:w-36 object-contain mb-1.5 drop-shadow-[0_8px_18px_rgba(0,0,0,0.6)]" /> :
              <div className="h-32 w-32 rounded-full bg-white/10 mb-1.5 flex items-center justify-center text-3xl font-blackhawk italic">{rightName.slice(0, 3).toUpperCase()}</div>}
            <div className="font-blackhawk italic uppercase text-white leading-tight text-center w-full break-words"
              style={{ fontSize: "clamp(1rem, 4.6vw, 1.4rem)" }}>{rightName}</div>
            <button
              onClick={() => onOpenRosterSide?.("away")}
              data-testid="perfs-open-roster-away-mobile"
              className="mt-2 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[11px] font-black uppercase tracking-[0.18em] transition active:scale-95 shadow-lg"
              style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.18), rgba(255,255,255,0.08))", color: "#fff", border: "1.5px solid rgba(255,255,255,0.35)" }}>
              <Users size={12}/> Voir l'effectif
            </button>
          </div>
        </div>

        {/* Rotating player cards (mobile) */}
        <div className="flex flex-col items-center pt-2">
          <div className="text-center text-[9px] uppercase tracking-[0.4em] text-white/45 font-bold mb-2">
            Joueurs · cycle automatique
          </div>
          <div className="grid grid-cols-2 gap-2 w-full max-w-2xl mx-auto" data-testid="perfs-double-carousel-live">
            <AnimatePresence mode="wait" initial={false}>
              {homePlayer ? (
                <PlayerCard key={`live-m-home-${idx}`} player={homePlayer} index={idx} compact side="home" ribbonLabel={(leftName||"").slice(0,4).toUpperCase()} onClick={() => onOpenPlayer?.(homePlayer.slug)}/>
              ) : (
                <div key="live-m-home-loading" className="text-center text-white/40 italic text-xs py-8">Chargement…</div>
              )}
            </AnimatePresence>
            <AnimatePresence mode="wait" initial={false}>
              {oppPlayer ? (
                <PlayerCard key={`live-m-away-${idx}`} player={oppPlayer} index={idx} compact side="away" ribbonLabel={(rightName||"").slice(0,4).toUpperCase()} onClick={() => onOpenPlayer?.(oppPlayer.slug)}/>
              ) : (
                <EmptyAwayCard key="live-m-away-empty" teamName={rightName} logo={rightLogo} compact />
              )}
            </AnimatePresence>
          </div>
          {maxLen > 1 && (
            <div className="mt-3 flex justify-center gap-1.5">
              {Array.from({ length: Math.min(maxLen, 16) }).map((_, i) => (
                <span key={i} className="block h-1 rounded-full transition-all"
                  style={{ width: i === (idx % 16) ? 16 : 5, background: i === (idx % 16) ? SECONDARY : "rgba(255,255,255,0.2)" }}/>
              ))}
            </div>
          )}
        </div>

        {/* Mobile events feed */}
        <div className="w-full mt-5 max-w-md mx-auto">
          {/* Stats Live IPHB compactes (mobile) */}
          <div className="grid grid-cols-3 gap-2 mb-4" data-testid="perfs-stats-live-mobile">
            <div className="rounded-lg border px-2 py-2 text-center"
                 style={{ background: `${SECONDARY}14`, borderColor: `${SECONDARY}55` }}>
              <div className="text-[10px] uppercase tracking-wider font-black text-pink-300/95">Tirs</div>
              <div className="font-black tabular-nums text-white leading-tight mt-0.5" style={{ fontSize: "clamp(1.1rem, 5.5vw, 1.5rem)", fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif" }}>{liveStats.iphbGoals}/{liveStats.iphbShots || 0}</div>
            </div>
            <div className="rounded-lg border px-2 py-2 text-center"
                 style={{ background: "rgba(34,211,238,0.10)", borderColor: "rgba(34,211,238,0.4)" }}>
              <div className="text-[10px] uppercase tracking-wider font-black" style={{ color: "#67E8F9" }}>Efficacité</div>
              <div className="font-black tabular-nums text-white leading-tight mt-0.5" style={{ fontSize: "clamp(1.1rem, 5.5vw, 1.5rem)", fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif" }}>{liveStats.efficiency === null ? "—" : `${liveStats.efficiency}%`}</div>
            </div>
            <div className="rounded-lg border px-2 py-2 text-center"
                 style={{ background: "rgba(34,211,238,0.10)", borderColor: "rgba(34,211,238,0.4)" }}>
              <div className="text-[10px] uppercase tracking-wider font-black" style={{ color: "#67E8F9" }}>Arrêts</div>
              <div className="font-black tabular-nums text-white leading-tight mt-0.5" style={{ fontSize: "clamp(1.1rem, 5.5vw, 1.5rem)", fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif" }}>{liveStats.iphbSaves}</div>
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2 text-center">Derniers événements</div>
          <div className="space-y-1.5" data-testid="perfs-events-feed-mobile">
            {(session?.events || []).slice(-5).reverse().map((ev) => (
              <div key={ev.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm">
                <span className="font-black uppercase shrink-0" style={{ color: ev.kind === "goal" ? SECONDARY : ev.kind === "gk_save" ? "#67E8F9" : ev.kind === "shot_miss" ? "#CBD5E1" : "#fff", fontSize: "0.85rem", letterSpacing: "0.04em" }}>
                  {ev.kind === "goal" ? "BUT" : ev.kind === "halftime" ? "MI-TEMPS" : ev.kind === "resume" ? "REPRISE" : ev.kind === "7m" ? "7M" : ev.kind === "2min" ? "2 MIN" : ev.kind === "card" ? "CARTON" : ev.kind === "timeout" ? "TIMEOUT" : ev.kind === "gk_save" ? "ARRÊT GK" : ev.kind === "shot_miss" ? "TIR ✕" : ev.kind.toUpperCase()}
                </span>
                {ev.player_name && <span className="text-white/80 truncate">{ev.player_name}</span>}
                <span className="ml-auto text-white/40 text-xs">{ev.side === "home" ? "IPHB" : ev.side === "away" ? "Adv." : ""}</span>
              </div>
            ))}
            {!session?.events?.length && <div className="text-center text-white/30 text-xs italic py-4">Aucun événement pour le moment</div>}
          </div>
        </div>
      </div>

      <div className="mt-auto pt-4">
        <div className="text-center text-[10px] text-white/40 py-2 uppercase tracking-[0.3em]">Suivez le match · #IPHB</div>
        <MscFooterCredit/>
      </div>

      <AnimatePresence>
        {goalEvent && <GoalAnimation event={goalEvent} onDone={onGoalDone} />}
      </AnimatePresence>

      {/* Carrousel d'offres mi-temps — full-screen overlay quand status=halftime */}
      <AnimatePresence>
        {session?.status === "halftime" && <HalftimeOffersCarousel session={session}/>}
      </AnimatePresence>
    </div>
  );
}

// ---------------------------------------------------------------
// HALFTIME OFFERS CAROUSEL — diffusion d'offres boutique + sponsors
// pendant la pause mi-temps. Plein écran, auto-rotate, dismiss auto
// dès que le statut repasse à "playing".
// ---------------------------------------------------------------
function HalftimeOffersCarousel({ session }) {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);

  // Charge produits boutique + offres partenaires une seule fois
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/sport/products").then((r) => (r.data || []).slice(0, 12)).catch(() => []),
      api.get("/sport/licensee/offers").then((r) => (r.data || []).slice(0, 8)).catch(() => []),
    ]).then(([products, offers]) => {
      if (cancelled) return;
      const productCards = (products || []).filter((p) => p.image || p.image_url || p.images?.[0]).map((p) => ({
        id: `prod-${p.id}`,
        kind: "product",
        title: p.name || p.title || "Produit IPHB",
        subtitle: p.brand || p.subtitle || "Boutique officielle",
        image: p.image || p.image_url || p.images?.[0] || "",
        price_cents: p.price ?? p.price_cents,
        old_price_cents: p.oldPrice ?? p.old_price_cents,
        sale_pct: p.sale_pct,
        cta_label: "Disponible en boutique",
        accent: SECONDARY,
        partner_logo: null,
        sector: p.category || p.group || "Boutique",
      }));
      const offerCards = (offers || []).map((o) => ({
        id: `offer-${o.id}`,
        kind: "offer",
        title: o.title || "Avantage licencié",
        subtitle: o.partner?.name || "Partenaire IPHB",
        image: o.partner?.logo_url || "",
        discount: o.discount_label,
        description: o.description,
        cta_label: o.code ? "Code disponible dans Mon Espace" : "Détails dans Mon Espace",
        accent: "#10B981",
        partner_logo: o.partner?.logo_url,
        sector: o.partner?.sector || "Avantages",
      }));
      // Mix : 1 produit, 1 offre, en alternance — fallback si une catégorie est vide
      const mixed = [];
      const maxLen = Math.max(productCards.length, offerCards.length);
      for (let i = 0; i < maxLen; i++) {
        if (productCards[i]) mixed.push(productCards[i]);
        if (offerCards[i]) mixed.push(offerCards[i]);
      }
      setItems(mixed);
    });
    return () => { cancelled = true; };
  }, []);

  // Auto-rotate toutes les 5s
  useEffect(() => {
    if (items.length === 0) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 5000);
    return () => clearInterval(t);
  }, [items.length]);

  if (items.length === 0) return null;
  const current = items[idx];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="fixed inset-0 z-[55] flex flex-col items-center justify-center px-6 py-10 overflow-hidden"
      style={{
        background: `linear-gradient(160deg, ${BG_DEEP} 0%, ${PRIMARY} 55%, #2a0e2c 100%)`,
      }}
      data-testid="halftime-offers-carousel"
    >
      {/* Fond animé : dots roses */}
      <div className="absolute inset-0 opacity-25 pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(circle, ${SECONDARY} 1px, transparent 1.5px)`,
          backgroundSize: "30px 30px",
          maskImage: "radial-gradient(ellipse at center, black, transparent 70%)",
        }}
      />

      {/* Header */}
      <div className="relative z-10 text-center mb-6">
        <motion.div
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          className="inline-block px-6 py-2 rounded-full border-2 mb-3"
          style={{
            borderColor: SECONDARY,
            background: `${SECONDARY}22`,
            boxShadow: `0 0 40px ${SECONDARY}66`,
          }}
        >
          <div className="text-[11px] font-black tracking-[0.4em] uppercase text-pink-300">
            Pause — Mi-temps
          </div>
        </motion.div>
        <h2 className="font-black uppercase italic text-white"
          style={{ fontFamily: "'Blackhawk', 'Bebas Neue', sans-serif", fontSize: "clamp(2rem, 6vw, 4rem)", letterSpacing: "0.04em", lineHeight: 1 }}>
          Les <span style={{ color: SECONDARY }}>bons plans</span>
        </h2>
        <div className="text-white/60 text-sm sm:text-base mt-2 font-bold">
          {session?.home_team || "IPHB"} · Score à la pause :
          <span className="text-white font-black mx-2 text-lg tabular-nums">
            {session?.home_score ?? 0}–{session?.away_score ?? 0}
          </span>
          · {session?.away_team || ""}
        </div>
      </div>

      {/* Carte courante */}
      <div className="relative z-10 w-full max-w-3xl flex-1 flex items-center justify-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={current.id}
            initial={{ opacity: 0, x: 80, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: -80, scale: 0.95 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="w-full rounded-3xl overflow-hidden shadow-2xl bg-white"
            data-testid={`halftime-card-${current.id}`}
          >
            {/* Bandeau type carte */}
            <div className="px-5 py-3 flex items-center justify-between"
              style={{ background: `linear-gradient(135deg, ${current.accent}, ${PRIMARY})` }}>
              <div className="flex items-center gap-2">
                {current.kind === "product" ? <ShoppingBag size={16} className="text-white"/> : <Gift size={16} className="text-white"/>}
                <span className="font-black uppercase tracking-widest text-[10px] sm:text-xs text-white">
                  {current.kind === "product" ? "Boutique officielle" : "Avantage licencié"}
                </span>
              </div>
              <div className="text-[10px] sm:text-xs font-black tracking-widest uppercase text-white/80">
                {current.sector}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-[1fr,1fr] gap-0">
              {/* Image / logo */}
              <div className="relative aspect-square sm:aspect-auto bg-slate-100 flex items-center justify-center p-6"
                style={{ minHeight: "260px" }}>
                {current.image ? (
                  <img
                    src={current.image}
                    alt={current.title}
                    className="max-w-full max-h-full object-contain"
                    loading="lazy"
                  />
                ) : (
                  <div className="text-7xl text-slate-300 font-black"
                    style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                    {(current.title || "?").charAt(0)}
                  </div>
                )}
                {current.discount && (
                  <div className="absolute top-3 right-3 px-3 py-1.5 rounded-full font-black uppercase text-white shadow-lg"
                    style={{ background: current.accent, fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.4rem", letterSpacing: "0.05em" }}>
                    {current.discount}
                  </div>
                )}
              </div>

              {/* Texte */}
              <div className="p-5 sm:p-7 flex flex-col justify-center">
                <div className="text-[11px] sm:text-[12px] uppercase tracking-[0.18em] font-black text-slate-500 mb-2">
                  {current.subtitle}
                </div>
                <h3 className="font-black text-slate-900 leading-[1.05]"
                  style={{ fontSize: "clamp(1.55rem, 4.2vw, 2.35rem)", letterSpacing: "-0.01em" }}>
                  {current.title}
                </h3>
                {current.description && (
                  <p className="text-base sm:text-lg text-slate-600 mt-3 leading-snug font-medium line-clamp-3">
                    {current.description}
                  </p>
                )}
                {current.kind === "product" && Number.isFinite(current.price_cents) && current.price_cents > 0 && (
                  <div className="mt-4 flex items-baseline gap-3">
                    <div className="inline-flex items-baseline gap-1">
                      <span className="font-black tabular-nums" style={{ color: PRIMARY, fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2.4rem, 7vw, 3.4rem)", lineHeight: 1 }}>
                        {(current.price_cents / 100).toFixed(2)}
                      </span>
                      <span className="text-2xl font-black" style={{ color: PRIMARY }}>€</span>
                    </div>
                    {Number.isFinite(current.old_price_cents) && current.old_price_cents > current.price_cents && (
                      <span className="text-base sm:text-lg text-slate-400 line-through font-bold tabular-nums">
                        {(current.old_price_cents / 100).toFixed(2)}€
                      </span>
                    )}
                    {Number.isFinite(current.sale_pct) && current.sale_pct > 0 && (
                      <span className="px-2 py-0.5 rounded-full text-white font-black text-[10px] uppercase tracking-widest"
                        style={{ background: "#ef4444" }}>
                        −{current.sale_pct}%
                      </span>
                    )}
                  </div>
                )}
                <div className="mt-4 inline-flex items-center gap-2 self-start px-4 py-2 rounded-full text-white font-black uppercase tracking-widest text-[11px] shadow-lg"
                  style={{ background: `linear-gradient(135deg, ${current.accent}, ${PRIMARY})` }}>
                  {current.cta_label} <ArrowRight size={12}/>
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Indicateurs progression (dots) */}
      <div className="relative z-10 flex items-center gap-1.5 mt-6" data-testid="halftime-progress">
        {items.slice(0, Math.min(items.length, 12)).map((_, i) => (
          <span
            key={i}
            className="h-1.5 rounded-full transition-all"
            style={{
              width: i === idx % Math.min(items.length, 12) ? 28 : 8,
              background: i === idx % Math.min(items.length, 12) ? SECONDARY : "rgba(255,255,255,0.25)",
            }}
          />
        ))}
      </div>
      <div className="relative z-10 text-[10px] uppercase tracking-[0.3em] text-white/40 mt-3 font-bold">
        Reprise du match dans quelques instants — restez connectés
      </div>
    </motion.div>
  );
}

// ---------------------------------------------------------------
// MODALS — full-screen overlays for Roster, Search, Player Detail
// ---------------------------------------------------------------
function ModalShell({ title, onClose, children, headerNode = null }) {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] overflow-y-auto"
      style={{ background: "rgba(5,0,16,0.92)", backdropFilter: "blur(8px)" }}
      data-testid="perfs-modal-shell"
    >
      <div className="min-h-full flex flex-col">
        <div className="sticky top-0 flex items-center justify-between px-4 py-3 border-b border-white/10"
          style={{ background: "rgba(13,2,32,0.95)" }}>
          {headerNode ? headerNode : (
            <div className="font-blackhawk italic uppercase tracking-widest text-white" style={{ fontSize: "clamp(1rem, 4vw, 1.4rem)" }}>{title}</div>
          )}
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/10 hover:bg-pink-500 text-white flex items-center justify-center transition" data-testid="perfs-modal-close">
            <X size={18}/>
          </button>
        </div>
        <div className="flex-1 px-3 py-4">{children}</div>
      </div>
    </motion.div>
  );
}

function MiniPlayerTile({ player, onClick }) {
  const number = player.jersey_number ?? "—";
  const name = (player.name || `${player.first_name || ""} ${player.last_name || ""}`).trim();
  return (
    <button onClick={onClick} data-testid={`perfs-roster-tile-${player.slug || player.id}`}
      className="group relative rounded-xl overflow-hidden border border-white/10 hover:border-pink-400 transition bg-gradient-to-b from-white/5 to-white/0 text-left">
      {player.photo_url && (
        <div className="aspect-[3/4] overflow-hidden bg-black/40">
          <img src={player.photo_url} alt={name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            onError={(e) => { e.currentTarget.style.display = "none"; }}/>
        </div>
      )}
      <div className="p-2">
        <div className="flex items-center gap-1">
          <span className="font-bebas font-black tabular-nums italic text-pink-400" style={{ fontSize: "clamp(0.9rem, 3vw, 1.1rem)" }}>{number}</span>
          <span className="text-white font-bold uppercase truncate text-[11px] sm:text-xs">{name}</span>
        </div>
        <div className="text-white/50 text-[9px] uppercase tracking-wider truncate">{player.position || ""}</div>
      </div>
    </button>
  );
}

// Group player position into broader buckets (synced with SportPlayersPage)
function rosterPositionBucket(p = "") {
  const s = (p || "").toLowerCase();
  if (s.includes("gard")) return "gardiens";
  if (s.includes("demi")) return "demi-centres";
  if (s.includes("ailier") && s.includes("gauche")) return "ailiers-gauche";
  if (s.includes("ailier") && s.includes("droit")) return "ailiers-droit";
  if (s.includes("arri") && s.includes("gauche")) return "arrieres-gauche";
  if (s.includes("arri") && s.includes("droit")) return "arrieres-droit";
  if (s.includes("pivot")) return "pivots";
  return "autres";
}

const ROSTER_POSITION_GROUPS = [
  { key: "gardiens", label: "Gardiens" },
  { key: "ailiers-gauche", label: "Ailiers Gauche" },
  { key: "arrieres-gauche", label: "Arrières Gauche" },
  { key: "demi-centres", label: "Demi-Centres" },
  { key: "arrieres-droit", label: "Arrières Droit" },
  { key: "ailiers-droit", label: "Ailiers Droit" },
  { key: "pivots", label: "Pivots" },
  { key: "autres", label: "Autres" },
];

function RosterPlayerCard({ p, onClick }) {
  const number = p.jersey_number ?? "—";
  const name = (p.name || `${p.first_name || ""} ${p.last_name || ""}`).trim() || "Joueur";
  const position = p.position || "Joueur";
  const ccode = p.country_code || "";
  const flagEmoji = p.country_flag_emoji || "";

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`perfs-roster-card-${p.slug || p.id}`}
      className="group relative block rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition focus:outline-none focus-visible:ring-4 focus-visible:ring-pink-500 text-left"
    >
      <div className="relative pb-[133%] overflow-hidden" style={{ background: `linear-gradient(180deg, ${PRIMARY} 0%, ${BG_DEEP} 100%)` }}>
        {/* diagonal stripes */}
        <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
          background: `repeating-linear-gradient(135deg, transparent 0 14px, rgba(255,255,255,0.04) 14px 16px)`,
        }}/>

        {/* photo */}
        {p.photo_url ? (
          <img src={p.photo_url} alt={name}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
            className="absolute inset-0 w-full h-full object-cover object-top transform-gpu group-hover:scale-[1.04] transition duration-500"/>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-white/30 font-bebas font-bold text-7xl tracking-widest text-center">
              {(name || "?").split(" ").map(s => s[0]).join("").slice(0, 2).toUpperCase()}
            </div>
          </div>
        )}

        {/* huge jersey number */}
        {number !== "—" && (
          <div className="absolute top-3 right-4 select-none pointer-events-none">
            <div className="text-white/15 group-hover:text-pink-400/40 transition font-bebas"
              style={{ fontSize: "clamp(3.5rem, 9vw, 7rem)", lineHeight: 1, transform: "rotate(-8deg)", fontWeight: 700 }}>
              {number}
            </div>
          </div>
        )}

        {/* flag inline avec le nom (bottom) — suffisant, ne chevauche plus le joueur */}

        {/* bottom name banner avec drapeau inline */}
        <div className="absolute inset-x-0 bottom-0"
          style={{ background: `linear-gradient(0deg, ${BG_DEEP} 60%, transparent 100%)` }}>
          <div className="px-2 pt-9 pb-3 text-center">
            <div className="font-black uppercase text-white tracking-tight inline-flex items-center justify-center gap-1.5 flex-wrap"
              style={{
                lineHeight: 1.1,
                wordBreak: "break-word",
                hyphens: "auto",
                fontSize: "clamp(0.95rem, 3.6vw, 1.1rem)",
              }}>
              {(ccode || flagEmoji) && (
                <span className="inline-flex shrink-0 rounded-sm overflow-hidden ring-1 ring-white/40 align-middle">
                  <Flag code={ccode} emoji={flagEmoji} size={16} label={p.country || ""}/>
                </span>
              )}
              <span>{name}</span>
            </div>
            <div className="text-pink-400 font-bold tracking-widest uppercase mt-1 truncate"
              style={{ fontSize: "clamp(0.7rem, 2.6vw, 0.8rem)" }}>{position}</div>
          </div>
        </div>
      </div>
    </button>
  );
}

function RosterModal({ players, staff = [], onClose, onPickPlayer, title = "Effectif complet", teamLogo = null, teamName = "", emptyLabel = "Aucun joueur disponible" }) {
  const grouped = useMemo(() => {
    const buckets = {};
    ROSTER_POSITION_GROUPS.forEach((g) => { buckets[g.key] = []; });
    (players || []).forEach((p) => {
      const k = rosterPositionBucket(p.position);
      buckets[k] = buckets[k] || [];
      buckets[k].push(p);
    });
    Object.values(buckets).forEach((arr) => arr.sort((a, b) => (a.jersey_number || 99) - (b.jersey_number || 99)));
    return buckets;
  }, [players]);

  const totalPlayers = (players || []).length;
  const totalStaff = (staff || []).length;

  const headerNode = (
    <div className="flex items-center gap-3 min-w-0">
      {teamLogo && (
        <img src={teamLogo} alt={teamName} className="h-10 w-10 sm:h-12 sm:w-12 object-contain drop-shadow-[0_4px_8px_rgba(0,0,0,0.5)] shrink-0"
          onError={(e) => { e.currentTarget.style.display = "none"; }}/>
      )}
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.3em] text-pink-300 font-bold leading-none truncate">{title}</div>
        {teamName && <div className="font-blackhawk italic uppercase text-white text-base sm:text-lg leading-tight mt-0.5 truncate">{teamName}</div>}
        <div className="text-[10px] text-white/50 mt-0.5">{totalPlayers} joueurs{totalStaff ? ` · ${totalStaff} staff` : ""}</div>
      </div>
    </div>
  );

  return (
    <ModalShell title={teamName ? "" : title} headerNode={teamName ? headerNode : null} onClose={onClose}>
      <div className="max-w-5xl mx-auto">
        {totalPlayers === 0 && totalStaff === 0 && (
          <div className="text-white/50 italic text-center py-12">{emptyLabel}</div>
        )}

        {ROSTER_POSITION_GROUPS.map((g) => {
          const list = grouped[g.key] || [];
          if (!list.length) return null;
          return (
            <div key={g.key} className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-[2px] flex-1 rounded-full" style={{ background: `linear-gradient(90deg, ${SECONDARY}, transparent)` }}/>
                <h3 className="font-black uppercase tracking-tight italic text-white text-sm sm:text-base whitespace-nowrap">
                  {g.label} <span className="text-pink-400">·</span> <span className="text-white/40 text-xs font-medium normal-case ml-1">{list.length}</span>
                </h3>
                <div className="h-[2px] flex-1 rounded-full" style={{ background: `linear-gradient(-90deg, ${SECONDARY}, transparent)` }}/>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                {list.map((p) => (
                  <RosterPlayerCard key={p.slug || p.id || p.name} p={p} onClick={() => onPickPlayer?.(p.slug)}/>
                ))}
              </div>
            </div>
          );
        })}

        {totalStaff > 0 && (
          <div className="mt-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-[2px] flex-1 rounded-full" style={{ background: `linear-gradient(90deg, ${SECONDARY}, transparent)` }}/>
              <h3 className="font-black uppercase tracking-tight italic text-white text-sm sm:text-base whitespace-nowrap">
                Encadrement · Coach &amp; Staff <span className="text-pink-400">·</span> <span className="text-white/40 text-xs font-medium normal-case ml-1">{staff.length}</span>
              </h3>
              <div className="h-[2px] flex-1 rounded-full" style={{ background: `linear-gradient(-90deg, ${SECONDARY}, transparent)` }}/>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {staff.map((s) => (
                <RosterPlayerCard key={s.slug || s.name} p={s} onClick={() => onPickPlayer?.(s.slug)}/>
              ))}
            </div>
          </div>
        )}
      </div>
    </ModalShell>
  );
}

function PlayerSearchModal({ players, awayPlayers, onClose, onPickPlayer, awayTeamLabel = "Adversaires" }) {
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all"); // all | home | away
  const all = useMemo(() => {
    const home = (players || []).map((p) => ({ ...p, _team: "IPHB", _side: "home" }));
    const away = (awayPlayers || []).map((p) => ({ ...p, _team: awayTeamLabel, _side: "away" }));
    return [...home, ...away];
  }, [players, awayPlayers, awayTeamLabel]);
  const counts = useMemo(() => ({
    all: all.length,
    home: all.filter((p) => p._side === "home").length,
    away: all.filter((p) => p._side === "away").length,
  }), [all]);
  const filtered = useMemo(() => {
    let pool = all;
    if (tab === "home") pool = pool.filter((p) => p._side === "home");
    if (tab === "away") pool = pool.filter((p) => p._side === "away");
    const needle = q.trim().toLowerCase();
    if (!needle) return pool.slice(0, 60);
    return pool.filter((p) => {
      const n = (p.name || `${p.first_name || ""} ${p.last_name || ""}`).toLowerCase();
      return n.includes(needle) || (String(p.jersey_number || "").includes(needle));
    });
  }, [q, all, tab]);

  const TabBtn = ({ id, label, n }) => (
    <button
      onClick={() => setTab(id)}
      data-testid={`perfs-search-tab-${id}`}
      className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-widest font-bold transition ${
        tab === id ? "text-white" : "text-white/50 hover:text-white"
      }`}
      style={tab === id ? { background: SECONDARY, boxShadow: `0 6px 16px ${SECONDARY}55` } : { background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }}
    >
      {label} <span className="opacity-60 ml-1">({n})</span>
    </button>
  );

  return (
    <ModalShell title="Rechercher un joueur" onClose={onClose}>
      <div className="max-w-xl mx-auto">
        <div className="relative mb-3">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"/>
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Nom, prénom ou numéro…"
            data-testid="perfs-search-input"
            className="w-full pl-9 pr-4 py-3 rounded-full bg-white/10 text-white placeholder:text-white/40 border border-white/15 focus:outline-none focus:border-pink-400"
          />
        </div>
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <TabBtn id="all" label="Tous" n={counts.all}/>
          <TabBtn id="home" label="IPHB" n={counts.home}/>
          <TabBtn id="away" label={awayTeamLabel} n={counts.away}/>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {filtered.map((p) => (
            <MiniPlayerTile key={`${p._side}-${p.slug || p.id}`} player={p} onClick={() => onPickPlayer?.(p.slug, p._side)} />
          ))}
        </div>
        {filtered.length === 0 && <div className="text-white/50 italic text-center py-12">Aucun résultat</div>}
      </div>
    </ModalShell>
  );
}

function PlayerDetailModal({ slug, onClose, sessionId }) {
  const { isAuthenticated, customer } = useCustomerAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voted, setVoted] = useState(false);
  const [voting, setVoting] = useState(false);
  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    api.get(`/sport/player/${slug}`)
      .then((r) => setData(r.data?.player || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const submitVote = async () => {
    if (!sessionId) {
      toast.error("Aucun match en cours — vote MVP indisponible");
      return;
    }
    if (!isAuthenticated) {
      onClose?.();
      navigate(`/sport/mon-espace?next=vote&player=${encodeURIComponent(slug)}&session=${encodeURIComponent(sessionId)}`);
      return;
    }
    setVoting(true);
    try {
      const token = localStorage.getItem("msc_customer_token");
      await api.post("/match-live/vote", {
        session_id: sessionId,
        player_slug: slug,
        player_name: data?.name || data?.first_name,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setVoted(true);
      toast.success(`Vote enregistré pour ${data?.first_name || data?.name || "ce joueur"} !`);
    } catch (e) {
      toast.error("Erreur lors de l'envoi du vote");
    } finally { setVoting(false); }
  };

  // Determine if the player is part of IPHB roster (only IPHB players are
  // votable). LNH player API exposes `team` field which we can compare.
  const isIPHB = data?.team && /istres|iphb|provence/i.test(data.team);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] overflow-y-auto"
      style={{ background: BG_DEEP }}
      data-testid="perfs-player-detail-modal"
    >
      {loading && <div className="text-white/50 italic text-center py-32">Chargement de la fiche…</div>}
      {!loading && !data && (
        <div className="text-white/50 italic text-center py-32">
          Fiche introuvable
          <button onClick={onClose} className="block mx-auto mt-4 px-5 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm uppercase tracking-widest font-bold">Fermer</button>
        </div>
      )}
      {data && (
        <div className="relative min-h-screen" data-testid="perfs-player-detail">
          {/* Background — full-bleed photo */}
          <div className="absolute inset-0 overflow-hidden">
            {data.photo_url ? (
              <img src={data.photo_url} alt={data.name}
                className="absolute inset-0 w-full h-full object-cover object-top"
                style={{ filter: "saturate(1.1) contrast(1.05)" }}/>
            ) : (
              <div className="absolute inset-0" style={{
                background: `radial-gradient(circle at top, ${PRIMARY} 0%, ${BG_DEEP} 80%)`,
              }}/>
            )}
            {/* Dark gradient overlay — keeps top photo readable, blurs/darkens bottom for text */}
            <div className="absolute inset-0 pointer-events-none"
              style={{
                background: `linear-gradient(180deg, rgba(5,0,16,0.25) 0%, rgba(5,0,16,0.55) 35%, rgba(5,0,16,0.92) 60%, ${BG_DEEP} 100%)`,
              }}/>
            {/* Bottom blur layer for crisp glass effect */}
            <div className="absolute inset-x-0 bottom-0 h-[58%] backdrop-blur-md pointer-events-none"
              style={{ maskImage: "linear-gradient(180deg, transparent 0%, black 12%)" }}/>
            {/* Subtle pink accent bloom */}
            <div className="absolute -top-10 -right-10 w-72 h-72 rounded-full pointer-events-none"
              style={{ background: `radial-gradient(circle, ${SECONDARY}33 0%, transparent 70%)` }}/>
          </div>

          {/* Close button */}
          <button onClick={onClose} data-testid="perfs-player-detail-close"
            className="fixed top-4 right-4 z-30 w-11 h-11 rounded-full bg-black/55 backdrop-blur-md hover:bg-pink-500 text-white flex items-center justify-center transition shadow-xl ring-1 ring-white/15">
            <X size={20}/>
          </button>

          {/* Watermark jersey number — top-right en BLANC */}
          {data.jersey_number != null && (
            <div className="absolute top-4 right-4 z-10 select-none pointer-events-none font-bebas tabular-nums text-right"
              style={{
                fontSize: "clamp(7rem, 28vw, 16rem)",
                lineHeight: 0.85,
                color: "#fff",
                opacity: 0.18,
                fontWeight: 700,
                letterSpacing: "-0.04em",
              }}>
              {data.jersey_number}
            </div>
          )}

          {/* Content — pushed to bottom 50% */}
          <div className="relative z-20 min-h-screen flex flex-col justify-end px-5 pb-8 pt-[40vh] sm:pt-[35vh]">
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
              className="max-w-xl mx-auto w-full"
            >
              {/* Position chip + flag */}
              <div className="flex items-center justify-center gap-2 mb-2 flex-wrap">
                {data.position && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-[0.3em] text-white"
                    style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)`, boxShadow: `0 6px 16px ${SECONDARY}66` }}>
                    {data.position}
                  </span>
                )}
                {(data.country_code || data.country_flag_emoji) && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 border border-white/15 text-white/85 text-[10px] uppercase font-bold tracking-wider">
                    <span className="inline-flex shrink-0 rounded-sm overflow-hidden ring-1 ring-white/30">
                      <Flag code={data.country_code} emoji={data.country_flag_emoji} size={14} label={data.country || ""}/>
                    </span>
                    {data.country || ""}
                  </span>
                )}
              </div>

              {/* Name */}
              <h2 className="font-blackhawk italic uppercase text-white text-center leading-[0.95]"
                style={{
                  fontSize: "clamp(2rem, 9vw, 4rem)",
                  textShadow: `0 6px 32px rgba(0,0,0,0.65), 0 0 16px ${SECONDARY}33`,
                  wordBreak: "break-word",
                }}>
                {data.name}
              </h2>
              {data.jersey_number != null && (
                <div className="text-center mt-1">
                  <span className="font-bebas font-black tabular-nums" style={{ color: SECONDARY, fontSize: "clamp(1.5rem, 5vw, 2.4rem)", textShadow: `0 0 18px ${SECONDARY}88` }}>
                    #{data.jersey_number}
                  </span>
                </div>
              )}

              {/* MVP Vote CTA — only for IPHB players, only during live match */}
              {isIPHB && sessionId && (
                <button
                  type="button"
                  onClick={voted || voting ? undefined : submitVote}
                  disabled={voted || voting}
                  data-testid="player-mvp-vote-cta"
                  className="mt-5 group block w-full rounded-2xl px-5 py-3.5 text-center font-black uppercase text-[12px] tracking-[0.18em] text-white relative overflow-hidden hover:scale-[1.02] active:scale-[0.99] transition shadow-2xl disabled:opacity-90 disabled:cursor-default"
                  style={{
                    background: voted
                      ? `linear-gradient(135deg, #16a34a, #22c55e)`
                      : `linear-gradient(135deg, ${SECONDARY}, #ff3a8c 60%, ${PRIMARY})`,
                    boxShadow: voted ? `0 12px 32px #22c55e88` : `0 12px 32px ${SECONDARY}88`,
                  }}
                >
                  {!voted && (
                    <span className="absolute inset-0 opacity-30 pointer-events-none"
                      style={{ background: "linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.45) 50%, transparent 70%)", animation: "iphbCardShine 2.6s ease-in-out infinite" }}/>
                  )}
                  <span className="relative inline-flex items-center justify-center gap-2 flex-wrap">
                    {!isAuthenticated ? <LogIn size={16} className="text-white"/> : <Award size={16} className="text-white"/>}
                    {voted
                      ? `Vote enregistré · Merci !`
                      : voting
                      ? `Envoi…`
                      : !isAuthenticated
                      ? `Me connecter pour voter ${(data.first_name || (data.name || "").split(" ")[0] || "ce joueur")}`
                      : `Voter pour ${(data.first_name || (data.name || "").split(" ")[0] || "ce joueur")} — MVP du match`}
                    {!voted && !voting && <ArrowRight size={14} className="group-hover:translate-x-1 transition"/>}
                  </span>
                  {!voted && !voting && (
                    <div className="relative text-pink-100/80 text-[10px] tracking-normal normal-case font-medium mt-1">
                      {isAuthenticated
                        ? `Connecté en tant que ${customer?.first_name || customer?.email}`
                        : "Compte gratuit · Suivez vos matchs et gagnez des lots"}
                    </div>
                  )}
                  <style>{`@keyframes iphbCardShine { 0% { transform: translateX(-120%); } 60% { transform: translateX(120%); } 100% { transform: translateX(120%); } }`}</style>
                </button>
              )}

              {/* Stats grid */}
              <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-5 text-center">
                {data.age && <StatChip label="Âge" value={`${data.age} ans`}/>}
                {data.birthdate && <StatChip label="Né le" value={data.birthdate}/>}
                {data.height_cm && <StatChip label="Taille" value={`${data.height_cm} cm`}/>}
                {data.weight_kg && <StatChip label="Poids" value={`${data.weight_kg} kg`}/>}
              </div>

              {data.team && (
                <div className="mt-3 flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-md">
                  {data.team_logo && <img src={data.team_logo} alt="" className="h-7 w-7 object-contain shrink-0"/>}
                  <div>
                    <div className="text-[9px] text-pink-300 uppercase tracking-[0.3em] font-bold">Club</div>
                    <div className="text-white font-black uppercase">{data.team}</div>
                  </div>
                </div>
              )}

              {data.career?.length > 0 && (
                <div className="mt-5">
                  <div className="text-pink-400 font-black tracking-[0.3em] text-[10px] uppercase mb-2 text-center">Parcours</div>
                  <div className="space-y-1">
                    {data.career.map((c, i) => (
                      <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm backdrop-blur-md">
                        <span className="text-pink-300 text-xs font-bold tabular-nums w-20 shrink-0">{c.season || c.years || ""}</span>
                        <span className="text-white truncate">{c.club || c.team || c.label || "—"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {data.lnh_url && (
                <div className="text-center mt-5">
                  <a href={data.lnh_url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-pink-300 hover:text-pink-200 text-[11px] uppercase tracking-widest font-bold underline">
                    Voir la fiche officielle LNH <ArrowRight size={11}/>
                  </a>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      )}
    </motion.div>
  );
}

function StatChip({ label, value }) {
  return (
    <div className="rounded-2xl px-3 py-2.5 backdrop-blur-md"
      style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }}>
      <div className="text-[9px] text-pink-300 uppercase tracking-[0.3em] font-bold">{label}</div>
      <div className="text-white font-black uppercase mt-0.5">{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------
// Big team panel (desktop)
// ---------------------------------------------------------------
function BigTeamPanel({ name, logo, side, label, standing, onOpenRoster }) {
  const accent = side === "away" ? "#fff" : SECONDARY;
  return (
    <div className="rounded-3xl p-6 text-center"
      style={{
        background: side === "away"
          ? `linear-gradient(160deg, #1a0820 0%, ${BG_DEEP} 80%)`
          : `linear-gradient(160deg, ${PRIMARY} 0%, ${BG_DEEP} 80%)`,
        border: "1px solid rgba(255,255,255,0.1)",
        boxShadow: `0 16px 40px rgba(0,0,0,0.55), 0 0 30px ${accent}33`,
      }}
    >
      {logo ? (
        <img src={logo} alt={name} className="h-28 w-28 mx-auto object-contain mb-3 drop-shadow-[0_10px_18px_rgba(0,0,0,0.6)]"
          onError={(e) => { e.currentTarget.style.display = "none"; }}/>
      ) : (
        <div className="h-28 w-28 mx-auto rounded-full bg-white/10 flex items-center justify-center font-black text-3xl text-white mb-3">
          {(name || "—").slice(0, 3).toUpperCase()}
        </div>
      )}
      <div className="text-[10px] uppercase tracking-[0.3em] font-bold mb-1" style={{ color: accent }}>
        {label || (side === "away" ? "Adversaire" : "À domicile")}
      </div>
      <div className="font-blackhawk italic uppercase text-white leading-tight" style={{ fontSize: "clamp(1.2rem, 2.4vw, 2rem)" }}>
        {name}
      </div>
      {standing?.rank && (
        <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-white text-[11px] uppercase tracking-widest font-bold"
          style={{ background: `linear-gradient(135deg, ${accent}30, transparent)`, border: `1px solid ${accent}55` }}
          data-testid={`perfs-${side}-rank`}>
          <span className="font-bebas font-black tabular-nums" style={{ fontSize: "1.35rem", color: accent, lineHeight: 1 }}>{standing.rank}<sup className="text-[0.6em]">e</sup></span>
          <span className="text-white/60">/</span>
          <span className="text-white/80">{standing.total || "—"}</span>
          <span className="text-white/40">·</span>
          <span>{standing.points} pts</span>
        </div>
      )}
      {onOpenRoster && (
        <button
          onClick={onOpenRoster}
          data-testid={`perfs-open-roster-${side}`}
          className="mt-5 w-full inline-flex items-center justify-center gap-2.5 px-5 py-3.5 rounded-2xl text-sm font-black uppercase tracking-[0.18em] transition hover:scale-[1.03] active:scale-[0.98] shadow-xl"
          style={{
            background: side === "away"
              ? "linear-gradient(135deg, rgba(255,255,255,0.18), rgba(255,255,255,0.08))"
              : `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)`,
            color: "#fff",
            border: side === "away" ? "1.5px solid rgba(255,255,255,0.35)" : "none",
            boxShadow: side === "away" ? "0 8px 22px rgba(0,0,0,0.4)" : `0 10px 26px ${SECONDARY}88`,
          }}
        >
          <Users size={18}/> Voir l'effectif
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------
// Presenter card
// ---------------------------------------------------------------
function PresenterCard({ photo, name }) {
  return (
    <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full"
      style={{
        background: "rgba(255,255,255,0.08)",
        border: "1px solid rgba(255,255,255,0.15)",
        backdropFilter: "blur(8px)",
      }}
      data-testid="perfs-presenter-card"
    >
      <div className="relative w-10 h-10 sm:w-11 sm:h-11 rounded-full overflow-hidden ring-2 flex-shrink-0"
        style={{ ringColor: SECONDARY, boxShadow: `0 0 0 2px ${SECONDARY}` }}>
        <img src={photo} alt={name} className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => { e.currentTarget.style.display = "none"; }}/>
      </div>
      <div className="text-left">
        <div className="text-[8px] sm:text-[9px] uppercase tracking-[0.3em] text-white/60 font-bold leading-none">Présenté par</div>
        <div className="font-blackhawk italic uppercase text-white text-base sm:text-lg leading-tight mt-0.5">{name}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
// Sponsor presenter card — "Match présenté par [Partenaire]"
// ---------------------------------------------------------------
function SponsorPresenterCard({ sponsor }) {
  if (!sponsor?.name && !sponsor?.logo_url) return null;
  return (
    <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full"
      style={{
        background: "linear-gradient(135deg, rgba(236,31,124,0.18), rgba(79,27,124,0.18))",
        border: "1px solid rgba(236,31,124,0.4)",
        backdropFilter: "blur(8px)",
      }}
      data-testid="perfs-sponsor-presenter-card"
    >
      <div className="text-left">
        <div className="text-[8px] sm:text-[9px] uppercase tracking-[0.3em] text-pink-300 font-bold leading-none">Match présenté par</div>
        <div className="font-blackhawk italic uppercase text-white text-base sm:text-lg leading-tight mt-0.5">{sponsor.name || "Partenaire"}</div>
      </div>
      {sponsor.logo_url && (
        <div className="flex items-center justify-center h-9 sm:h-10 px-2 rounded-md bg-white/95">
          <img src={sponsor.logo_url} alt={sponsor.name || ""} className="h-7 sm:h-8 w-auto object-contain"
            onError={(e) => { e.currentTarget.style.display = "none"; }}/>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------
// MSC FOOTER CREDIT — bas de page
// ---------------------------------------------------------------
function MscFooterCredit() {
  return (
    <a
      href="/lancez-votre-site"
      target="_blank"
      rel="noopener noreferrer"
      className="block group"
      data-testid="perfs-msc-credit"
    >
      <div className="flex items-center justify-center gap-2 sm:gap-3 py-2 sm:py-3 text-[9px] sm:text-[10px] text-white/40 group-hover:text-white/70 uppercase tracking-[0.3em] transition">
        <span>Solution propulsée par</span>
        <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-md bg-white/10 group-hover:bg-white/20 border border-white/15 group-hover:border-pink-400/60 transition">
          <img src={MSC_LOGO} alt="Agence MSC"
            className="h-5 sm:h-6 w-auto object-contain"
            style={{ filter: "brightness(0) invert(1)" }}
            loading="lazy"/>
        </span>
      </div>
    </a>
  );
}

// ---------------------------------------------------------------
// MAIN COMPONENT
// ---------------------------------------------------------------
export default function SportPerfsWall() {
  const [search] = useSearchParams();
  const isDemo = search.get("demo") === "1";
  const kickoffOverride = search.get("kickoff");
  const awayOverride = search.get("away");
  const awayLogoOverride = search.get("away_logo");

  // Module désactivable — si l'admin a coupé sport_perfs_wall_enabled, on bloque l'accès.
  const { settings: gs } = useSettings();
  const moduleDisabled = gs && gs.sport_perfs_wall_enabled === false;

  const [session, setSession] = useState(null);
  const [partners, setPartners] = useState([]);
  const [players, setPlayers] = useState([]);
  const [awayPlayers, setAwayPlayers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [nextMatch, setNextMatch] = useState(null);
  const [standing, setStanding] = useState(null);
  const [oppStanding, setOppStanding] = useState(null);
  const [goalEvent, setGoalEvent] = useState(null);
  const [showRoster, setShowRoster] = useState(null); // null | "home" | "away"
  const [showSearch, setShowSearch] = useState(false);
  const [activePlayerSlug, setActivePlayerSlug] = useState(null);
  // Mode démo manuel — cycle pre / mt1 / halftime / mt2 / end
  const [demoPhase, setDemoPhase] = useState(null); // null | "pre" | "mt1" | "halftime" | "mt2" | "end"
  const lastEventIdRef = useRef(null);

  // Demo synthesizes a kickoff in 5 minutes if no override is given
  const demoKickoff = useMemo(() => {
    if (!isDemo) return null;
    if (kickoffOverride) return kickoffOverride;
    return new Date(Date.now() + 5 * 60 * 1000).toISOString();
  }, [isDemo, kickoffOverride]);

  // Polling session
  useEffect(() => {
    let alive = true;
    const fetchSession = async () => {
      try {
        const r = await api.get("/match-live/active");
        if (!alive) return;
        setSession(r.data?.session || null);
      } catch { /* swallow */ }
    };
    fetchSession();
    const id = setInterval(fetchSession, 4000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // Charge l'effectif adverse depuis la LNH dès qu'on connaît son slug/nom
  useEffect(() => {
    let oppName = null;
    if (awayOverride) oppName = awayOverride;
    else if (isDemo) oppName = null; // demo sans away param → pas de roster
    else if (nextMatch) {
      const isHome = /istres|iphb/i.test(nextMatch.home_team || "");
      oppName = (isHome ? nextMatch.away_team : nextMatch.home_team) || null;
    }
    const oppSlug = oppName ? oppName.toLowerCase().replace(/\s+/g, "-") : null;
    if (!oppSlug) { setAwayPlayers([]); return; }
    let alive = true;
    api.get(`/sport/team-roster?slug=${encodeURIComponent(oppSlug)}`)
      .then((r) => { if (alive) setAwayPlayers(r.data?.players || []); })
      .catch(() => { if (alive) setAwayPlayers([]); });
    return () => { alive = false; };
  }, [nextMatch, isDemo, awayOverride]);
  useEffect(() => {
    const loadAll = async () => {
      try {
        const [pa, ti, nm, st] = await Promise.all([
          api.get("/sport/partners").catch(() => ({ data: [] })),
          api.get("/sport/team-info").catch(() => ({ data: {} })),
          api.get("/sport/next-match").catch(() => ({ data: {} })),
          api.get("/sport/standings").catch(() => ({ data: {} })),
        ]);
        setPartners(pa.data || []);
        const list = (((ti.data || {}).info || {}).players || ti.data?.players || []);
        const stf = (((ti.data || {}).info || {}).staff || ti.data?.staff || []);
        setPlayers(list);
        setStaff(stf);
        setNextMatch(nm.data?.match || null);
        const rows = (st.data?.rows || st.data?.standings || []);
        const total = rows.length;
        const myRow = rows.find((r) => /istres/i.test((r.team_name || r.team || "")));
        setStanding(myRow ? { ...myRow, total } : null);
        // Opp standing — selon nextMatch
        const m = nm.data?.match;
        if (m) {
          const isHome = /istres|iphb/i.test(m.home_team || "");
          const oppTeamName = (isHome ? m.away_team : m.home_team) || "";
          const oppRow = oppTeamName ? rows.find((r) => (r.team_name || r.team || "").toLowerCase().includes(oppTeamName.toLowerCase())) : null;
          setOppStanding(oppRow ? { ...oppRow, total } : null);
        } else {
          setOppStanding(null);
        }
      } catch { /* swallow */ }
    };
    loadAll();
    const id = setInterval(loadAll, 60000);
    return () => clearInterval(id);
  }, []);

  // Détecte un nouveau goal -> déclenche l'animation
  useEffect(() => {
    if (!session?.last_event) return;
    const ev = session.last_event;
    if (ev.id === lastEventIdRef.current) return;
    lastEventIdRef.current = ev.id;
    if (ev.kind === "goal") setGoalEvent(ev);
  }, [session]);

  // Empêche scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  // Build the effective match for pre-match scene
  const preMatch = useMemo(() => {
    if (isDemo) {
      return {
        home_team: "Istres Provence Handball",
        home_logo: "/api/sport/asset?url=https%3A%2F%2Flnh.fr%2Fmedias%2Fsports_teams%2Fistres__logo__2023-2024.png",
        away_team: awayOverride || "Adversaire Démo",
        away_logo: awayLogoOverride || null,
        venue: "Halle Polyvalente · Istres",
        league: "Liqui Moly StarLigue",
        round: "DEMO",
        matchday: "Match de démonstration",
      };
    }
    if (!nextMatch) return null;
    return {
      ...nextMatch,
      away_team: awayOverride || nextMatch.away_team,
      away_logo: awayLogoOverride || nextMatch.away_logo,
    };
  }, [isDemo, nextMatch, awayOverride, awayLogoOverride]);

  const effectiveKickoff = useMemo(() => {
    if (isDemo) return demoKickoff;
    if (kickoffOverride) return kickoffOverride;
    if (!nextMatch) return null;
    // Build ISO from date + time fields
    const d = nextMatch.date_iso || nextMatch.date;
    if (!d) return null;
    const t = nextMatch.time || "20:00";
    // Assume Europe/Paris -> store as local ISO without TZ; browser will treat as local
    return `${d}T${t}:00`;
  }, [isDemo, demoKickoff, kickoffOverride, nextMatch]);

  // Synthesise une session démo selon la phase choisie
  const demoSession = useMemo(() => {
    if (!demoPhase || demoPhase === "pre") return null;
    const home_team = "Istres Provence Handball";
    const away_team = "Dunkerque";
    const home_logo = "/api/sport/asset?url=https%3A%2F%2Flnh.fr%2Fmedias%2Fsports_teams%2Fistres__logo__2023-2024.png";
    const away_logo = "/api/sport/asset?url=https%3A%2F%2Flnh.fr%2Fmedias%2Fsports_teams%2Fdunkerque__logo__2023-2024.png";
    const baseEvents = [
      { id: "e1", kind: "goal", side: "home", player_name: "Camara", t: "02:14" },
      { id: "e2", kind: "goal", side: "away", player_name: "Billant", t: "04:52" },
      { id: "e3", kind: "goal", side: "home", player_name: "Mathias #7m", t: "07:18" },
      { id: "e4", kind: "2min", side: "away", player_name: "Pollard", t: "09:30" },
      { id: "e5", kind: "goal", side: "home", player_name: "Romero", t: "12:01" },
      { id: "e6", kind: "goal", side: "away", player_name: "Konan", t: "14:38" },
      { id: "e7", kind: "card", side: "home", player_name: "Marie-Joseph", t: "17:52" },
      { id: "e8", kind: "goal", side: "home", player_name: "Camara", t: "21:20" },
    ];
    if (demoPhase === "mt1") {
      // Mid-1ère mi-temps : ~22 min, 11-9
      const startMs = Date.now() - 22 * 60 * 1000 - 13 * 1000;
      return {
        id: "demo-mt1", home_team, away_team, home_logo, away_logo,
        status: "playing", period: 1, home_score: 11, away_score: 9,
        chrono_started_at: new Date(startMs).toISOString(),
        chrono_paused_ms: 0, chrono_paused_at: null,
        events: baseEvents.slice(0, 6),
      };
    }
    if (demoPhase === "halftime") {
      // Mi-temps : 15-13
      return {
        id: "demo-ht", home_team, away_team, home_logo, away_logo,
        status: "halftime", period: 1, home_score: 15, away_score: 13,
        period_1_duration_ms: 30 * 60 * 1000, period_2_duration_ms: 0,
        chrono_started_at: null, chrono_paused_ms: 0, chrono_paused_at: null,
        events: [...baseEvents, { id: "e9", kind: "halftime", t: "30:00" }],
      };
    }
    if (demoPhase === "mt2") {
      // Mi-2ème mi-temps : ~14 min, 24-22
      const startMs = Date.now() - 14 * 60 * 1000 - 7 * 1000;
      return {
        id: "demo-mt2", home_team, away_team, home_logo, away_logo,
        status: "playing", period: 2, home_score: 24, away_score: 22,
        period_1_duration_ms: 30 * 60 * 1000,
        chrono_started_at: new Date(startMs).toISOString(),
        chrono_paused_ms: 0, chrono_paused_at: null,
        events: [
          ...baseEvents,
          { id: "e9", kind: "halftime", t: "30:00" },
          { id: "e10", kind: "resume", t: "30:00" },
          { id: "e11", kind: "goal", side: "home", player_name: "Romero", t: "32:14" },
          { id: "e12", kind: "goal", side: "away", player_name: "Pollard", t: "35:02" },
          { id: "e13", kind: "goal", side: "home", player_name: "Camara", t: "38:46" },
        ],
      };
    }
    if (demoPhase === "end") {
      // Match terminé : 32-29
      return {
        id: "demo-end", home_team, away_team, home_logo, away_logo,
        status: "finished", period: 2, home_score: 32, away_score: 29,
        period_1_duration_ms: 30 * 60 * 1000, period_2_duration_ms: 30 * 60 * 1000,
        chrono_started_at: null, chrono_paused_ms: 0, chrono_paused_at: null,
        finished_at: new Date().toISOString(),
        events: [
          ...baseEvents,
          { id: "e9", kind: "halftime", t: "30:00" },
          { id: "e10", kind: "resume", t: "30:00" },
          { id: "e11", kind: "goal", side: "home", player_name: "Romero", t: "32:14" },
          { id: "e12", kind: "goal", side: "away", player_name: "Pollard", t: "35:02" },
          { id: "e13", kind: "goal", side: "home", player_name: "Camara", t: "38:46" },
          { id: "e14", kind: "goal", side: "home", player_name: "Mathias", t: "44:11" },
          { id: "e15", kind: "goal", side: "away", player_name: "Konan", t: "48:25" },
          { id: "e16", kind: "card", side: "away", player_name: "Billant", t: "52:18" },
          { id: "e17", kind: "goal", side: "home", player_name: "Romero", t: "56:42" },
          { id: "e18", kind: "goal", side: "away", player_name: "Konan", t: "59:11" },
        ],
      };
    }
    return null;
  }, [demoPhase]);

  // Decide which scene to show
  const effectiveSession = demoSession || session;
  const isLive = effectiveSession && effectiveSession.status !== "finished";
  // Show pre-match if not live AND we have an upcoming match (within 12h) or demo,
  // OR if user explicitly chose demoPhase === "pre"
  const showPrematch = !isLive && !demoSession && (
    demoPhase === "pre" || isDemo ||
    (preMatch && effectiveKickoff && (() => {
      const t = new Date(effectiveKickoff).getTime();
      const ms = t - Date.now();
      return !isNaN(t) && ms > -60 * 60 * 1000 && ms < 12 * 60 * 60 * 1000;
    })())
  );
  // For "end" demo phase we still want to show LiveScene (status: finished is OK)
  const showFinished = effectiveSession && effectiveSession.status === "finished";

  // Module désactivé par l'administrateur du club → message d'information
  if (moduleDisabled) {
    return (
      <div
        className="fixed inset-0 flex items-center justify-center text-white p-8"
        style={{ background: `radial-gradient(circle at top, ${PRIMARY} 0%, ${BG_DEEP} 50%, #050010 100%)` }}
        data-testid="perfs-wall-disabled"
      >
        <div className="max-w-md w-full text-center space-y-5">
          <div className="text-6xl">🔒</div>
          <h1 className="text-3xl font-black uppercase tracking-wider">Perfs Wall désactivé</h1>
          <p className="text-white/70 leading-relaxed">
            L&apos;administrateur du club a temporairement désactivé l&apos;écran live diffusable.<br />
            Revenez ici plus tard ou contactez le club pour plus d&apos;informations.
          </p>
          <Link
            to="/sport"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-slate-900 font-bold uppercase tracking-wider text-sm hover:bg-pink-200 transition"
            data-testid="perfs-wall-disabled-back"
          >
            Retour au site
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative w-screen min-h-screen overflow-hidden text-white"
      style={{
        background: `radial-gradient(circle at top, ${PRIMARY} 0%, ${BG_DEEP} 50%, #050010 100%)`,
        fontFamily: "'Bebas Neue', 'Inter', sans-serif",
      }}
      data-testid="perfs-wall-root"
    >
      {/* Bouton Retour au site — toujours visible (au-dessus de tout) */}
      <Link
        to="/sport"
        data-testid="perfs-back-to-site"
        className="fixed top-4 left-4 z-[100] inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full font-bold uppercase tracking-[0.2em] text-[10px] sm:text-xs text-white shadow-lg transition active:scale-95 hover:scale-[1.03]"
        style={{
          background: "rgba(0,0,0,0.55)",
          backdropFilter: "blur(14px)",
          border: `1px solid ${SECONDARY}55`,
          boxShadow: `0 8px 22px rgba(0,0,0,0.45), 0 0 16px ${SECONDARY}33`,
        }}
        aria-label="Retour au site IPHB"
      >
        <ArrowLeft size={14}/>
        <span className="hidden sm:inline">Retour au site</span>
        <span className="sm:hidden">Site</span>
      </Link>
      <BullWatermark/>

      {isLive ? (
        <LiveScene
          session={effectiveSession}
          partners={partners}
          players={players}
          awayPlayers={awayPlayers}
          goalEvent={goalEvent}
          onGoalDone={() => setGoalEvent(null)}
          onOpenPlayer={(slug) => setActivePlayerSlug(slug)}
          onOpenRosterSide={(side) => setShowRoster(side)}
        />
      ) : showFinished ? (
        <>
          <LiveScene
            session={effectiveSession}
            partners={partners}
            players={players}
            awayPlayers={awayPlayers}
            goalEvent={null}
            onGoalDone={() => {}}
            onOpenPlayer={(slug) => setActivePlayerSlug(slug)}
            onOpenRosterSide={(side) => setShowRoster(side)}
          />
          <MvpWinnerOverlay sessionId={effectiveSession?.id} players={players} demoSession={demoSession}/>
        </>
      ) : showPrematch ? (
        <PreMatchScene
          match={preMatch}
          kickoffIso={effectiveKickoff}
          partners={partners}
          players={players}
          awayPlayers={awayPlayers}
          standing={standing}
          oppStanding={oppStanding}
          onOpenRoster={() => setShowRoster("home")}
          onOpenRosterSide={(side) => setShowRoster(side)}
          onOpenSearch={() => setShowSearch(true)}
          onOpenPlayer={(slug) => setActivePlayerSlug(slug)}
        />
      ) : (
        <IdleScene
          partners={partners}
          nextMatch={nextMatch}
          onLaunchDemo={() => setDemoPhase("pre")}
        />
      )}

      {/* MODALS */}
      <AnimatePresence>
        {showRoster && (() => {
          const isHomeMatch = nextMatch ? /istres|iphb/i.test(nextMatch.home_team || "") : true;
          const oppName = (() => {
            if (awayOverride) return awayOverride;
            if (nextMatch) return isHomeMatch ? nextMatch.away_team : nextMatch.home_team;
            return "Adversaire";
          })();
          const oppLogo = (() => {
            if (awayLogoOverride) return awayLogoOverride;
            if (nextMatch) return isHomeMatch ? nextMatch.away_logo : nextMatch.home_logo;
            return null;
          })();
          if (showRoster === "home") {
            return (
              <RosterModal
                players={players}
                staff={staff}
                title="Effectif IPHB"
                teamName="Istres Provence Handball"
                teamLogo={IPHB_LOGO}
                emptyLabel="Effectif IPHB en cours de chargement…"
                onClose={() => setShowRoster(null)}
                onPickPlayer={(slug) => { setShowRoster(null); setActivePlayerSlug(slug); }}
              />
            );
          }
          return (
            <RosterModal
              players={awayPlayers}
              staff={[]}
              title="Effectif adverse"
              teamName={oppName}
              teamLogo={oppLogo}
              emptyLabel={`Effectif ${oppName || "adverse"} indisponible (équipe non listée par la LNH).`}
              onClose={() => setShowRoster(null)}
              onPickPlayer={(slug) => { setShowRoster(null); setActivePlayerSlug(slug); }}
            />
          );
        })()}
        {showSearch && (
          <PlayerSearchModal
            players={players}
            awayPlayers={awayPlayers}
            awayTeamLabel={(() => {
              if (awayOverride) return awayOverride;
              if (nextMatch) {
                const isHome = /istres|iphb/i.test(nextMatch.home_team || "");
                return isHome ? nextMatch.away_team : nextMatch.home_team;
              }
              return "Adversaires";
            })()}
            onClose={() => setShowSearch(false)}
            onPickPlayer={(slug) => { setShowSearch(false); setActivePlayerSlug(slug); }}
          />
        )}
        {activePlayerSlug && (
          <PlayerDetailModal
            slug={activePlayerSlug}
            sessionId={effectiveSession?.id}
            onClose={() => setActivePlayerSlug(null)}
          />
        )}
      </AnimatePresence>

      {/* MODE DÉMO — bouton flottant en bas de page (toujours visible) */}
      <DemoControls phase={demoPhase} onChange={setDemoPhase}/>
    </div>
  );
}

// ---------------------------------------------------------------
// PRONO CARD — Pronostic du score exact (pre-match + 1ʳᵉ MT)
// ---------------------------------------------------------------
function PronoCard({ session, homeTeam, awayTeam }) {
  const cfg = useGamesConfig(session?.id);
  const [home, setHome] = useState("");
  const [away, setAway] = useState("");
  const [name, setName] = useState("");
  const [mine, setMine] = useState(null);
  const [busy, setBusy] = useState(false);

  const sessionId = session?.id;
  const status = session?.status;
  const period = session?.period;
  // Verrouillé après mi-temps (halftime atteinte) ou si match fini
  const locked = !sessionId || status === "halftime" || status === "finished" || (status === "playing" && period === 2);

  useEffect(() => {
    if (!sessionId) return;
    api.get(`/match-live/prono/mine?session_id=${sessionId}&voter_key=${getVoterKey()}`)
      .then((r) => {
        const p = r.data?.prono;
        if (p) {
          setMine(p);
          setHome(String(p.home_pred));
          setAway(String(p.away_pred));
          setName(p.voter_name || "");
        }
      }).catch(() => {});
  }, [sessionId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!sessionId || locked) return;
    const hp = parseInt(home, 10);
    const ap = parseInt(away, 10);
    if (isNaN(hp) || isNaN(ap) || hp < 0 || ap < 0 || hp > 99 || ap > 99) {
      toast.error("Score invalide (0-99)");
      return;
    }
    setBusy(true);
    try {
      await api.post("/match-live/prono", {
        session_id: sessionId,
        voter_key: getVoterKey(),
        voter_name: (name || "Anonyme").slice(0, 40),
        home_pred: hp, away_pred: ap,
      });
      setMine({ home_pred: hp, away_pred: ap, voter_name: name || "Anonyme", submitted_at: new Date().toISOString() });
      toast.success(`Pronostic ${hp}-${ap} enregistré !`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Erreur lors de l'envoi";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  if (!sessionId) {
    // Pre-match strict (pas de session) : juste teaser
    return (
      <div className="rounded-2xl p-4 sm:p-5 border border-pink-400/30 backdrop-blur-md"
        style={{ background: "rgba(236,31,124,0.08)" }}
        data-testid="prono-card-teaser">
        <div className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-pink-300 font-bold mb-1.5">🎯 Jeu Pronostic</div>
        <div className="text-white text-sm sm:text-base font-bold">Devinez le score exact, gagnez un lot !</div>
        <div className="text-white/65 text-xs mt-1">Le formulaire s'activera dès le coup d'envoi.</div>
        {cfg.prono_prize && (
          <div className="mt-2 text-pink-300 text-[11px] uppercase tracking-wide">Lot : {cfg.prono_prize}</div>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-2xl p-4 sm:p-5 border border-pink-400/40 backdrop-blur-md"
      style={{ background: "linear-gradient(135deg, rgba(236,31,124,0.18), rgba(79,27,124,0.18))" }}
      data-testid="prono-card">
      <div className="flex items-center gap-2 mb-3">
        <div className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-pink-300 font-bold">🎯 Jeu Pronostic</div>
        {locked && <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/10 text-white/70">Verrouillé</span>}
      </div>
      {mine ? (
        <div>
          <div className="text-white/80 text-xs mb-1">Votre pronostic :</div>
          <div className="font-black text-3xl sm:text-4xl text-white tracking-tight tabular-nums" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            {mine.home_pred} <span className="text-pink-300">·</span> {mine.away_pred}
          </div>
          <div className="text-white/50 text-[11px] mt-1">{mine.voter_name} · {locked ? "Validé" : "Modifiable jusqu'à mi-temps"}</div>
        </div>
      ) : null}
      {!locked && (
        <form onSubmit={submit} className="mt-3 space-y-2.5" data-testid="prono-form">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[9px] uppercase tracking-wider text-white/50 mb-1">{(homeTeam || "Domicile").slice(0, 12)}</label>
              <input type="number" min="0" max="99" value={home} onChange={(e) => setHome(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg bg-black/40 border border-white/15 text-white text-2xl text-center font-black tabular-nums focus:outline-none focus:border-pink-400"
                data-testid="prono-home-input"/>
            </div>
            <div>
              <label className="block text-[9px] uppercase tracking-wider text-white/50 mb-1">{(awayTeam || "Extérieur").slice(0, 12)}</label>
              <input type="number" min="0" max="99" value={away} onChange={(e) => setAway(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg bg-black/40 border border-white/15 text-white text-2xl text-center font-black tabular-nums focus:outline-none focus:border-pink-400"
                data-testid="prono-away-input"/>
            </div>
          </div>
          <input type="text" placeholder="Votre prénom (visible si vous gagnez)" value={name} onChange={(e) => setName(e.target.value)} maxLength={40}
            className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/15 text-white text-sm focus:outline-none focus:border-pink-400"
            data-testid="prono-name-input"/>
          <button type="submit" disabled={busy || !home || !away}
            className="w-full py-2.5 rounded-lg font-black uppercase tracking-wider text-xs text-white disabled:opacity-50 transition"
            style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)`, boxShadow: `0 6px 18px ${SECONDARY}66` }}
            data-testid="prono-submit">
            {busy ? "Envoi…" : mine ? "Modifier mon prono" : "Valider mon pronostic"}
          </button>
        </form>
      )}
      {(cfg.prono_prize || cfg.prono_sponsor_name) && (
        <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-2.5">
          {cfg.prono_sponsor_logo && (
            <img src={cfg.prono_sponsor_logo} alt={cfg.prono_sponsor_name} className="h-7 w-auto object-contain bg-white/95 rounded p-1"/>
          )}
          <div className="flex-1 min-w-0">
            {cfg.prono_prize && <div className="text-pink-300 text-[10px] uppercase tracking-wider font-bold">Lot · {cfg.prono_prize}</div>}
            {cfg.prono_sponsor_name && <div className="text-white/70 text-[10px]">offert par {cfg.prono_sponsor_name}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------
// PRONO WINNER CARD — affiché en fin de match
// ---------------------------------------------------------------
function PronoWinnerCard({ session }) {
  const cfg = useGamesConfig(session?.id);
  const [winner, setWinner] = useState(null);
  const [exact, setExact] = useState(false);
  useEffect(() => {
    if (!session?.id) return;
    api.get(`/match-live/prono/winner?session_id=${session.id}`)
      .then((r) => { setWinner(r.data?.winner || null); setExact(!!r.data?.exact); })
      .catch(() => {});
  }, [session?.id]);

  if (!winner) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay: 0.4, type: "spring", stiffness: 180 }}
      className="rounded-3xl p-5 sm:p-6 border-2 backdrop-blur-md"
      style={{
        background: `linear-gradient(135deg, rgba(255,215,0,0.12), rgba(236,31,124,0.18))`,
        borderColor: "#FFD700",
        boxShadow: "0 18px 60px rgba(255,215,0,0.25)",
      }}
      data-testid="prono-winner-card"
    >
      <div className="text-center">
        <div className="text-3xl sm:text-4xl mb-2">🏆</div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-yellow-300 font-bold mb-1">
          {exact ? "Pronostic gagnant — Score exact !" : "Pronostic le plus proche"}
        </div>
        <div className="font-black uppercase italic text-white text-3xl sm:text-4xl mb-1" style={{ fontFamily: "'Blackhawk', 'Bebas Neue', sans-serif" }}>
          {winner.voter_name}
        </div>
        <div className="text-white/85 text-base sm:text-lg font-black tabular-nums mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
          {winner.home_pred} - {winner.away_pred}
        </div>
        {cfg.prono_prize && (
          <div className="inline-block px-4 py-2 rounded-full bg-yellow-300/20 border border-yellow-300/40">
            <div className="text-yellow-200 text-[10px] uppercase tracking-wider font-bold">Récupérez votre lot à l'accueil</div>
            <div className="text-white text-sm font-bold mt-0.5">{cfg.prono_prize}</div>
          </div>
        )}
        {cfg.prono_sponsor_name && (
          <div className="mt-3 flex items-center justify-center gap-2 text-white/65 text-[11px]">
            {cfg.prono_sponsor_logo && <img src={cfg.prono_sponsor_logo} alt="" className="h-6 w-auto object-contain bg-white/95 rounded p-0.5"/>}
            <span>Offert par {cfg.prono_sponsor_name}</span>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ---------------------------------------------------------------
// IDLE SCENE — Aucun match, présentation de la solution + accès consoles
// ---------------------------------------------------------------
function IdleScene({ partners, nextMatch, onLaunchDemo }) {
  const features = [
    { Icon: Activity,    title: "Score live & chrono", desc: "Saisie temps réel par l'opérateur, chrono mi-temps automatique, animations BUT." },
    { Icon: Users,       title: "Carrousel joueurs",   desc: "Cycle automatique des effectifs IPHB et adverse en cartes immersives FIFA-style." },
    { Icon: Trophy,      title: "Vote MVP",            desc: "Les supporters votent l'homme du match. Résultat affiché en fin de rencontre." },
    { Icon: Target,      title: "Jeu Pronostic",       desc: "Devinez le score exact, gagnez un lot offert par un partenaire." },
    { Icon: ShoppingBag, title: "Offres mi-temps",     desc: "Carrousel d'offres boutique et bons sponsors pendant la pause." },
    { Icon: Tv,          title: "Diffusion live",      desc: "Affichage QR/TV stade, mode portrait, polling automatique 2s." },
  ];

  return (
    <div className="relative z-10 flex flex-col min-h-screen">
      <PartnersMarquee partners={partners} />

      <div className="flex-1 px-5 sm:px-8 lg:px-16 py-10 sm:py-12">
        {/* HERO */}
        <div className="text-center max-w-4xl mx-auto mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[11px] uppercase tracking-[0.3em] font-bold mb-6"
            style={{ background: "rgba(236,31,124,0.15)", border: "1px solid rgba(236,31,124,0.4)", color: "#ff7eb6" }}>
            <span className="w-2 h-2 rounded-full bg-pink-400 animate-pulse"/>
            Aucun match en cours
          </div>
          <h1 className="font-black uppercase italic leading-none mb-5"
            style={{
              fontFamily: "'Blackhawk', 'Bebas Neue', sans-serif",
              fontSize: "clamp(3rem, 9vw, 6rem)",
              background: `linear-gradient(135deg, #fff 0%, ${SECONDARY} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}>
            PERFS WALL
          </h1>
          <p className="text-white/85 leading-relaxed max-w-2xl mx-auto"
            style={{ fontSize: "clamp(1rem, 2.4vw, 1.35rem)" }}>
            L'écran de match digital diffusé dans le stade pendant les rencontres IPHB.
            Score, chrono, joueurs, votes, pronostics — tout en temps réel et 100% personnalisé pour le club.
          </p>
        </div>

        {/* DEMO CTA */}
        <div className="max-w-2xl mx-auto mb-14 sm:mb-20">
          <button
            type="button"
            onClick={onLaunchDemo}
            data-testid="idle-launch-demo"
            className="w-full px-6 py-6 sm:py-7 rounded-2xl font-black uppercase tracking-[0.15em] text-white shadow-2xl transition active:scale-[0.98] hover:scale-[1.01]"
            style={{
              background: `linear-gradient(135deg, ${SECONDARY} 0%, #ff3a8c 100%)`,
              boxShadow: `0 18px 50px ${SECONDARY}66`,
              fontSize: "clamp(1.05rem, 4vw, 1.4rem)",
            }}
          >
            <span className="flex items-center justify-center gap-3">
              <Sparkles size={22}/>
              Lancer la démo interactive
            </span>
            <span className="block text-[11px] sm:text-xs opacity-85 mt-2 tracking-[0.25em] font-normal">
              Visualisez les 6 scènes du match · Avant · Mi-temps · Live · Final
            </span>
          </button>
          {nextMatch && (
            <div className="mt-5 text-center text-xs sm:text-sm text-white/55 uppercase tracking-[0.25em]">
              Prochain match : <span className="text-white font-bold">{nextMatch.home_team} vs {nextMatch.away_team}</span> · {nextMatch.date_iso}
            </div>
          )}
        </div>

        {/* FEATURES GRID */}
        <div className="max-w-5xl mx-auto mb-14 sm:mb-20">
          <div className="text-center text-xs sm:text-sm uppercase tracking-[0.3em] text-pink-300 font-bold mb-6">
            Fonctionnalités embarquées
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 sm:gap-4">
            {features.map((f, i) => (
              <div
                key={i}
                className="rounded-2xl p-5 sm:p-6 backdrop-blur-md border transition hover:border-pink-400/50"
                style={{ background: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.1)" }}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: `linear-gradient(135deg, ${SECONDARY}30, ${PRIMARY}40)`, border: "1px solid rgba(236,31,124,0.4)" }}>
                  <f.Icon size={24} className="text-pink-300" strokeWidth={1.75}/>
                </div>
                <div className="font-black uppercase text-base sm:text-lg text-white tracking-wide mb-1.5 leading-tight">{f.title}</div>
                <div className="text-sm text-white/70 leading-relaxed">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* CONSOLE ACCESS — séparation visuelle forte */}
        <div className="relative max-w-5xl mx-auto pt-10 sm:pt-12">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-pink-400/40 to-transparent"/>
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-3"
              style={{ background: "rgba(236,31,124,0.1)", border: "1px solid rgba(236,31,124,0.3)" }}>
              <Gamepad2 size={14} className="text-pink-300"/>
              <span className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-pink-300 font-bold">
                Espaces de pilotage
              </span>
            </div>
            <h2 className="font-black uppercase italic text-white"
              style={{ fontFamily: "'Blackhawk', 'Bebas Neue', sans-serif", fontSize: "clamp(1.75rem, 5vw, 2.5rem)" }}>
              Accès staff
            </h2>
            <div className="text-white/55 text-sm sm:text-base mt-1.5">
              Consoles sécurisées par PIN — accès démo à tester directement.
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ConsoleCard
              to="/sport/operator"
              testId="idle-console-operator"
              Icon={Gamepad2}
              title="Console Opérateur"
              role="Saisie temps réel"
              desc="Démarrage match, saisie buts, mi-temps, fin de match."
              accent={SECONDARY}
              demoPin={{ key: "iphb_operator_pin", value: "1970", label: "Opérateur" }}
            />
            <ConsoleCard
              to="/sport/speaker"
              testId="idle-console-speaker"
              Icon={Mic}
              title="Console Speaker"
              role="Stade · Fabrice"
              desc="Lecture seule. Recherche joueurs, ticker live, fiches détaillées."
              accent="#fbbf24"
              demoPin={{ key: "iphb_speaker_pin", value: "5678", label: "Speaker" }}
            />
            <ConsoleCard
              to="/sport/games-admin"
              testId="idle-console-games"
              Icon={Trophy}
              title="Gestion des jeux"
              role="MVP · Pronos · Lots"
              desc="Pilotage du vote MVP, du jeu pronostic, configuration des lots et sponsors."
              accent="#22d3ee"
              demoPin={{ key: "iphb_operator_pin", value: "1970", label: "Admin Jeux" }}
            />
          </div>

          {/* DÉMO BANDEAU — accès rapide tous les comptes */}
          <div className="mt-6 rounded-2xl p-4 sm:p-5 border backdrop-blur-md"
            style={{ background: "linear-gradient(135deg, rgba(236,31,124,0.08), rgba(34,211,238,0.06))", borderColor: "rgba(255,255,255,0.12)" }}>
            <div className="flex items-start sm:items-center gap-3 flex-col sm:flex-row sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: "rgba(236,31,124,0.18)", border: "1px solid rgba(236,31,124,0.4)" }}>
                  <Sparkles size={20} className="text-pink-300" strokeWidth={1.75}/>
                </div>
                <div>
                  <div className="text-pink-300 text-[10px] uppercase tracking-[0.25em] font-bold">Mode démo</div>
                  <div className="text-white font-black text-base sm:text-lg leading-tight">Accès rapide tous profils</div>
                  <div className="text-white/55 text-xs sm:text-sm mt-0.5">Aucun PIN à saisir, pour la présentation client.</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                <DemoQuickAccess to="/sport/operator" pinKey="iphb_operator_pin" pin="1970" label="Opérateur" Icon={Gamepad2} accent={SECONDARY}/>
                <DemoQuickAccess to="/sport/speaker" pinKey="iphb_speaker_pin" pin="5678" label="Speaker" Icon={Mic} accent="#fbbf24"/>
                <DemoQuickAccess to="/sport/games-admin" pinKey="iphb_operator_pin" pin="1970" label="Admin" Icon={Trophy} accent="#22d3ee"/>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FOOTER : barre d'accès rapide consoles + crédit MSC */}
      <div className="mt-auto">
        <div className="border-t border-white/10 backdrop-blur-md" style={{ background: "rgba(13,2,32,0.6)" }}>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center justify-center gap-2 sm:gap-3">
            <span className="text-[10px] sm:text-xs uppercase tracking-[0.25em] text-white/45 font-bold mr-1">Accès rapide :</span>
            <FooterAccessLink to="/sport/operator" Icon={Gamepad2} label="Opérateur"/>
            <FooterAccessLink to="/sport/speaker" Icon={Mic} label="Speaker"/>
            <FooterAccessLink to="/sport/games-admin" Icon={Trophy} label="Jeux"/>
          </div>
        </div>
        <div className="text-center text-[11px] text-white/40 py-2 uppercase tracking-[0.3em]">
          Suivez le match · #IPHB
        </div>
        <MscFooterCredit/>
      </div>
    </div>
  );
}

function FooterAccessLink({ to, Icon, label }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 hover:border-pink-400/40 text-white/80 hover:text-white transition text-[11px] sm:text-xs uppercase tracking-wider font-bold"
      data-testid={`footer-access-${label.toLowerCase()}`}
    >
      <Icon size={13} strokeWidth={2.2}/>
      {label}
    </Link>
  );
}

// Bouton compact "Démo · {profil}" — pré-remplit le PIN dans localStorage
// puis ouvre la console correspondante directement loggée.
function DemoQuickAccess({ to, pinKey, pin, label, Icon, accent }) {
  const handleClick = () => {
    try { localStorage.setItem(pinKey, pin); } catch (e) { /* noop */ }
  };
  return (
    <Link
      to={to}
      onClick={handleClick}
      data-testid={`demo-quick-${label.toLowerCase()}`}
      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full font-black uppercase text-[11px] tracking-[0.18em] text-white shadow-lg active:scale-95 transition"
      style={{
        background: `linear-gradient(135deg, ${accent}, ${accent}cc)`,
        border: `1px solid ${accent}`,
        boxShadow: `0 6px 18px ${accent}55`,
      }}
    >
      <Icon size={13} strokeWidth={2.4}/>
      {label}
    </Link>
  );
}

function ConsoleCard({ to, testId, Icon, title, role, desc, accent, demoPin }) {
  const handleDemoClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (demoPin) {
      try { localStorage.setItem(demoPin.key, demoPin.value); } catch (err) { /* noop */ }
      window.location.href = to;
    }
  };
  return (
    <Link
      to={to}
      data-testid={testId}
      className="group relative block rounded-2xl p-6 backdrop-blur-md border transition hover:scale-[1.02] active:scale-[0.99] overflow-hidden"
      style={{
        background: `linear-gradient(180deg, rgba(255,255,255,0.06) 0%, ${accent}10 100%)`,
        borderColor: `${accent}40`,
      }}
    >
      <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full opacity-20 group-hover:opacity-40 transition" style={{ background: accent, filter: "blur(40px)" }}/>
      <div className="relative">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 transition group-hover:scale-110"
          style={{ background: `${accent}20`, border: `1.5px solid ${accent}80` }}>
          <Icon size={26} style={{ color: accent }} strokeWidth={1.75}/>
        </div>
        <div className="font-black uppercase text-lg text-white tracking-wide leading-tight mb-1">{title}</div>
        <div className="text-[11px] uppercase tracking-[0.2em] mb-3 font-bold" style={{ color: accent }}>{role}</div>
        <div className="text-sm text-white/70 leading-relaxed mb-4 min-h-[3em]">{desc}</div>
        <div className="flex items-center justify-between gap-2">
          <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-white font-bold group-hover:gap-2.5 transition-all">
            Accéder
            <ChevronRight size={16} style={{ color: accent }}/>
          </div>
          {demoPin && (
            <button
              type="button"
              onClick={handleDemoClick}
              data-testid={`${testId}-demo`}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold transition hover:opacity-90"
              style={{ background: `${accent}25`, color: accent, border: `1px solid ${accent}55` }}
              title={`Connexion auto avec PIN ${demoPin.value}`}
            >
              <Sparkles size={11}/> Démo
            </button>
          )}
        </div>
      </div>
    </Link>
  );
}

function DemoControls({ phase, onChange }) {
  const [open, setOpen] = useState(false);
  const phases = [
    { id: null,        label: "Live réel",       hint: "Données réelles" },
    { id: "pre",       label: "Avant match",     hint: "Compteur, joueurs cyclants" },
    { id: "mt1",       label: "1ʳᵉ mi-temps",   hint: "Score 11-9 · 22'" },
    { id: "halftime",  label: "Mi-temps",        hint: "15-13 · pause" },
    { id: "mt2",       label: "2ᵉ mi-temps",    hint: "24-22 · 14'" },
    { id: "end",       label: "Fin du match",    hint: "32-29 · final" },
  ];
  // Position: top-right partout (évite chevauchement avec marquee sponsor en bas)
  return (
    <div className="fixed top-3 right-3 z-[55] pointer-events-none">
      <div className="flex flex-col items-end gap-2 pointer-events-auto">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          data-testid="perfs-demo-toggle"
          className="inline-flex items-center gap-2 px-3 py-2 sm:px-4 sm:py-2.5 rounded-full font-black uppercase text-[10px] sm:text-[11px] tracking-[0.18em] text-white shadow-2xl border transition"
          style={{
            background: phase ? `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` : "rgba(13,2,32,0.85)",
            borderColor: phase ? SECONDARY : "rgba(255,255,255,0.25)",
            backdropFilter: "blur(10px)",
            boxShadow: phase ? `0 10px 28px ${SECONDARY}66` : "0 10px 28px rgba(0,0,0,0.6)",
          }}
        >
          <span className={`w-2 h-2 rounded-full ${phase ? "bg-white animate-pulse" : "bg-pink-400"}`}/>
          {phase ? `Démo · ${(phases.find(x => x.id === phase) || {}).label}` : "Mode démo"}
        </button>

        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl p-2 backdrop-blur-xl shadow-2xl"
            style={{ background: "rgba(13,2,32,0.92)", border: "1px solid rgba(255,255,255,0.18)" }}
            data-testid="perfs-demo-panel"
          >
            <div className="text-[9px] uppercase tracking-[0.3em] text-pink-300 font-bold text-center mb-2 px-2">
              Mode démo · Choisir une scène
            </div>
            <div className="grid grid-cols-2 gap-1.5 max-w-[280px]">
              {phases.map((p) => {
                const active = phase === p.id;
                return (
                  <button
                    key={String(p.id)}
                    type="button"
                    onClick={() => { onChange(p.id); setOpen(false); }}
                    data-testid={`demo-phase-${p.id || "live"}`}
                    className={`text-left px-3 py-2 rounded-xl transition ${active ? "shadow-lg" : "hover:bg-white/5"}`}
                    style={{
                      background: active ? `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` : "rgba(255,255,255,0.05)",
                      border: active ? "1px solid #ff3a8c" : "1px solid rgba(255,255,255,0.1)",
                    }}
                  >
                    <div className={`font-black uppercase text-[11px] ${active ? "text-white" : "text-white/90"} tracking-wide`}>
                      {p.label}
                    </div>
                    <div className={`text-[9px] ${active ? "text-white/85" : "text-white/45"} mt-0.5`}>
                      {p.hint}
                    </div>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
